// ============================================================
//  ALTIMO Medical Center — Formularios.jsx  v3
//  Formularios completos según FORMATOS_REVISADOS_2025.xlsx
//  Nuevos tipos: siNo, tabla_dinamica, grupo_sinos, check_cant
// ============================================================

import React, { useState, useEffect } from 'react';
import api from '../apiClient';
import PrescripcionesPanel from './PrescripcionesPanel';
import OrdenesLabPanel from './OrdenesLabPanel';

// ── Utilidades ───────────────────────────────────────────────
function calcularEdad(fechaNac) {
  if (!fechaNac) return '';
  const hoy = new Date(); const nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad + ' años';
}

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const DIAS_SEMANA = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

function dos(n) { return String(n).padStart(2, '0'); }

function fechaISOHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${dos(d.getMonth()+1)}-${dos(d.getDate())}`;
}

function aNumeroEntero(v) {
  const limpio = String(v ?? '').replace(/[^\d.-]/g, '');
  const n = Number(limpio);
  return Number.isFinite(n) ? n : NaN;
}

function numeroALetrasEntero(n) {
  const UNIDADES = ['cero','uno','dos','tres','cuatro','cinco','seis','siete','ocho','nueve'];
  const DIEZ_A_QUINCE = ['diez','once','doce','trece','catorce','quince'];
  const DECENAS = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
  const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

  n = Math.trunc(Math.abs(Number(n) || 0));
  if (n < 10) return UNIDADES[n];
  if (n >= 10 && n <= 15) return DIEZ_A_QUINCE[n - 10];
  if (n < 20) return 'dieci' + numeroALetrasEntero(n - 10);
  if (n === 20) return 'veinte';
  if (n < 30) return 'veinti' + numeroALetrasEntero(n - 20);
  if (n < 100) {
    const d = Math.floor(n / 10), r = n % 10;
    return r ? `${DECENAS[d]} y ${numeroALetrasEntero(r)}` : DECENAS[d];
  }
  if (n === 100) return 'cien';
  if (n < 1000) {
    const c = Math.floor(n / 100), r = n % 100;
    return r ? `${CENTENAS[c]} ${numeroALetrasEntero(r)}` : CENTENAS[c];
  }
  if (n < 2000) {
    const r = n % 1000;
    return r ? `mil ${numeroALetrasEntero(r)}` : 'mil';
  }
  if (n < 1000000) {
    const m = Math.floor(n / 1000), r = n % 1000;
    const miles = `${numeroALetrasEntero(m)} mil`;
    return r ? `${miles} ${numeroALetrasEntero(r)}` : miles;
  }
  if (n < 2000000) {
    const r = n % 1000000;
    return r ? `un millón ${numeroALetrasEntero(r)}` : 'un millón';
  }
  if (n < 1000000000000) {
    const m = Math.floor(n / 1000000), r = n % 1000000;
    const millones = `${numeroALetrasEntero(m)} millones`;
    return r ? `${millones} ${numeroALetrasEntero(r)}` : millones;
  }
  return String(n);
}

function normalizarUnoMoneda(txt) {
  return txt
    .replace(/\buno millones\b/g, 'un millones')
    .replace(/\buno mil\b/g, 'un mil')
    .replace(/\buno\b/g, 'un');
}

function montoDolaresALetras(v) {
  const n = Number(String(v ?? '').replace(/,/g, '').trim());
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  const enteros = Math.floor(abs);
  const centavos = Math.round((abs - enteros) * 100);
  let letras = normalizarUnoMoneda(numeroALetrasEntero(enteros));
  letras = letras.charAt(0).toUpperCase() + letras.slice(1);
  return `${letras} con ${String(centavos).padStart(2, '0')}/100`;
}

function limpiarMonto(v) {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function calcularMontoCuota(monto, cuotas) {
  const total = limpiarMonto(monto);
  const nCuotas = Math.trunc(Number(cuotas));
  if (!total || !nCuotas || nCuotas < 1) return '';
  return (total / nCuotas).toFixed(2);
}

function fechaPagareEnLetras(iso) {
  if (!iso) return '';
  const [y,m,d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '';
  const f = new Date(y, m - 1, d);
  if (Number.isNaN(f.getTime())) return '';
  const diaSemana = DIAS_SEMANA[f.getDay()] || '';
  const diaMesNum = d;
  const diaMesLetras = numeroALetrasEntero(diaMesNum);
  const mes = MESES[m - 1] || '';
  return `${diaSemana} ${diaMesNum} (${diaMesLetras}) de ${mes}`;
}

// ── Estilos ──────────────────────────────────────────────────
const S = {
  pagina:  { background:'#f0f4f8', minHeight:'100vh', padding:'20px', fontFamily:'Segoe UI, sans-serif' },
  card:    { background:'#fff', borderRadius:'10px', border:'1px solid #dde3ea', padding:'18px', marginBottom:'14px' },
  titulo:  { fontSize:'12px', fontWeight:'700', color:'#1B2A5E', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'14px', paddingBottom:'10px', borderBottom:'2px solid #e8f0fb', display:'flex', justifyContent:'space-between', alignItems:'center' },
  input:   { width:'100%', fontSize:'13px', padding:'8px 10px', border:'1px solid #c0ccda', borderRadius:'7px', background:'#fff', color:'#333', outline:'none', boxSizing:'border-box' },
  label:   { display:'block', fontSize:'11px', fontWeight:'600', color:'#6c757d', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'.04em' },
  campo:   { marginBottom:'13px' },
  fila2:   { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' },
  fila3:   { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px' },
  btnP:    { background:'#1B2A5E', color:'#fff', border:'none', borderRadius:'7px', padding:'9px 20px', fontSize:'13px', fontWeight:'700', cursor:'pointer' },
  btnV:    { background:'#1D9E75', color:'#fff', border:'none', borderRadius:'7px', padding:'9px 20px', fontSize:'13px', fontWeight:'700', cursor:'pointer' },
  btnS:    { background:'#fff', color:'#495057', border:'1px solid #c0ccda', borderRadius:'7px', padding:'8px 16px', fontSize:'13px', cursor:'pointer' },
  fcard:   (color) => ({ border:`1px solid ${color}30`, borderLeft:`4px solid ${color}`, borderRadius:'8px', padding:'11px 14px', background:'#fff', cursor:'pointer', transition:'all .12s' }),
  aH:      { fontSize:'11px', fontWeight:'700', color:'#fff', padding:'5px 12px', borderRadius:'6px', marginBottom:'8px', display:'inline-block' },
  fgrid:   { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px, 1fr))', gap:'9px', marginBottom:'16px' },
  // Estilos para nuevos tipos de campo
  sinoWrap:{ display:'flex', alignItems:'center', gap:'14px', padding:'8px 12px', background:'#f8fafd', borderRadius:'7px', border:'1px solid #c0ccda' },
  sinoBtn: (activo, color) => ({ padding:'6px 16px', borderRadius:'6px', fontSize:'13px', fontWeight:'700', cursor:'pointer', border:'none', background: activo ? color : '#e8f0fb', color: activo ? '#fff' : '#6c757d' }),
  tabDin:  { width:'100%', borderCollapse:'collapse', fontSize:'12px', marginTop:'6px' },
  tabTh:   { background:'#1B2A5E', color:'#fff', padding:'7px 8px', textAlign:'left', fontSize:'11px', fontWeight:'600' },
  tabTd:   { padding:'4px 4px', borderBottom:'1px solid #eef1f6' },
  tabIn:   { width:'100%', fontSize:'12px', padding:'5px 6px', border:'1px solid #c0ccda', borderRadius:'4px', outline:'none', boxSizing:'border-box' },
  secH:    { fontSize:'11px', fontWeight:'700', color:'#1B2A5E', textTransform:'uppercase', letterSpacing:'.05em', margin:'16px 0 8px', paddingBottom:'4px', borderBottom:'2px solid #e8f0fb' },
  checkItem: { display:'flex', alignItems:'center', gap:'8px', padding:'6px 10px', borderRadius:'6px', fontSize:'12px', border:'1px solid #dde3ea', background:'#f8fafd', marginBottom:'4px' },
};

// ── Catálogos ─────────────────────────────────────────────────
const AREAS = [
  { id:'admision',    label:'Admisión',             color:'#185FA5' },
  { id:'clinico',     label:'Clínico / Médico',     color:'#534AB7' },
  { id:'enfermeria',  label:'Enfermería',            color:'#0F6E56' },
  { id:'quirofano',   label:'Quirófano',             color:'#993C1D' },
  { id:'facturacion', label:'Facturación',           color:'#854F0B' },
  { id:'inventario',  label:'Inventario / Farmacia', color:'#3B6D11' },
  { id:'rrhh',        label:'RRHH / Admin',          color:'#5F5E5A' },
];

const FORMULARIOS = [
  { id:'ingreso_egreso',      area:'admision',    nombre:'Hoja de ingreso y egreso' },
  { id:'consentimiento',      area:'admision',    nombre:'Consentimiento informado' },
  { id:'ingreso_enfermera',   area:'enfermeria',  nombre:'Ingreso de enfermería' },
  { id:'pagare',              area:'admision',    nombre:'Pagaré sin protesto' },
  { id:'evolucion',           area:'clinico',     nombre:'Hoja de evolución médica' },
  { id:'ordenes_medico',      area:'clinico',     nombre:'Órdenes del médico' },
  { id:'control_medicos',     area:'clinico',     nombre:'Control de médicos tratantes' },
  { id:'chequeo_quirurgico',  area:'clinico',     nombre:'Chequeo previo quirúrgico' },
  { id:'signos_vitales',      area:'enfermeria',  nombre:'Hoja de signos vitales' },
  { id:'reporte_enfermera',   area:'enfermeria',  nombre:'Reporte de enfermería' },
  { id:'temp_pulso',          area:'enfermeria',  nombre:'Temperatura y pulso' },
  { id:'balance_hidrico',     area:'enfermeria',  nombre:'Control balance hídrico' },
  { id:'tarjeta_medicamento', area:'enfermeria',  nombre:'Tarjeta de medicamentos' },
  { id:'tarjeta_oral',        area:'enfermeria',  nombre:'Tarjeta vía oral' },
  { id:'tarjeta_parenteral',  area:'enfermeria',  nombre:'Tarjeta parenteral' },
  { id:'prog_cirugias',       area:'quirofano',   nombre:'Programación de cirugías' },
  { id:'gastos_sala',         area:'quirofano',   nombre:'Hoja de gastos sala de operación' },
  { id:'reporte_anestesia',   area:'quirofano',   nombre:'Reporte de anestesia en quirófano' },
  { id:'reporte_operatorio',  area:'quirofano',   nombre:'Reporte operatorio' },
  { id:'honorarios',          area:'facturacion', nombre:'Honorarios médicos' },
  { id:'asignaciones',        area:'facturacion', nombre:'Asignaciones' },
  { id:'reporte_insumos',     area:'inventario',  nombre:'Reporte de insumos utilizados' },
  { id:'req_productos',       area:'inventario',  nombre:'Requisición de productos médicos' },
  { id:'control_temp_refri',  area:'inventario',  nombre:'Control temperatura refrigeradora' },
  { id:'lista_asistencia',    area:'rrhh',        nombre:'Lista de asistencia' },
  { id:'acciones_personal',   area:'rrhh',        nombre:'Acciones de personal' },
];

// ════════════════════════════════════════════════════════════
//  GENERADORES PDF (HTML que se imprime)
// ════════════════════════════════════════════════════════════
function wrap(contenido) {
  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"/>
  <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Segoe UI',sans-serif;color:#222;padding:24px;font-size:12px}
  h3{color:#1B2A5E;font-size:11px;text-transform:uppercase;letter-spacing:.05em;margin:12px 0 5px;border-bottom:1px solid #e8f0fb;padding-bottom:3px}
  table{width:100%;border-collapse:collapse;font-size:11px;margin:5px 0}
  th{background:#1B2A5E;color:#fff;padding:5px 7px;text-align:left;font-size:10px}
  td{padding:5px 7px;border-bottom:1px solid #eee}
  .c{display:flex;gap:6px;margin-bottom:6px}.cl{font-weight:700;min-width:130px;font-size:11px}.cv{border-bottom:1px solid #333;flex:1;min-height:15px;font-size:12px;padding:0 2px}
  .f2{display:grid;grid-template-columns:1fr 1fr;gap:10px}
  .f3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}
  .f4{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:8px}
  .fs{display:flex;gap:16px;margin-top:24px}.fi{flex:1;text-align:center;border-top:1px solid #333;padding-top:5px;font-size:11px}
  .cab{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px}
  .sino{display:inline-flex;gap:12px;margin-left:8px;font-size:12px}
  .sino span{font-weight:600}
  hr{border:none;border-top:2px solid #1B2A5E;margin:6px 0 12px}
  .bloque{border:1px solid #ccc;min-height:80px;padding:6px;border-radius:4px;font-size:12px;line-height:1.8;margin:4px 0 8px}
  @media print{body{padding:10px}}</style></head><body>${contenido}
  <script>window.onload=()=>window.print()</script></body></html>`;
}

function cab(titulo, fecha) {
  return `<div class="cab"><div><div style="font-size:18px;font-weight:800;color:#1B2A5E">ALTIMO</div><div style="font-size:9px;letter-spacing:2px;color:#1D9E75">MEDICAL CENTER</div></div><div><div style="font-size:13px;font-weight:700;color:#1B2A5E;text-align:right">${titulo}</div><div style="font-size:11px;color:#666;text-align:right">Fecha: ${fecha||new Date().toLocaleDateString('es-SV')}</div></div></div><hr/>`;
}
const cv  = (l,v) => `<div class="c"><span class="cl">${l}:</span><span class="cv">${v||''}</span></div>`;
// fir() genera la fila de firmas al pie del documento.
// Cuando d._firma_b64 está disponible, el primer firmante (médico/enfermera) incluye la imagen.
function fir(d, ...fs) {
  const firmaHtml = (d && d._firma_b64)
    ? `<div style="margin:6px auto 4px;text-align:center;min-height:64px;display:flex;align-items:center;justify-content:center"><img src="${d._firma_b64}" style="max-height:68px;max-width:220px;object-fit:contain"/></div>` +
      `<div style="font-size:10px;color:#333;line-height:1.35">${d._usuario_titulo ? d._usuario_titulo+' ' : ''}${d._usuario_nombre || ''}</div>`
    : '';
  return `<div class="fs">${fs.map((f, i) =>
    `<div class="fi">${i === 0 ? firmaHtml : ''}${f}</div>`
  ).join('')}</div>`;
}
const sino = (v) => `<span class="sino"><span>${v==='si'?'☑':'☐'} Sí</span><span>${v==='no'?'☑':'☐'} No</span></span>`;
function tbl(cols, filas) {
  return `<table><thead><tr>${cols.map(c=>`<th>${c}</th>`).join('')}</tr></thead><tbody>${filas.map(f=>`<tr>${cols.map((_,i)=>`<td>${f[i]||'&nbsp;'}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
}
const rows = (n, cols) => Array(n).fill(null).map(() => cols.map(()=>''));

const GEN = {
  ingreso_egreso: (p,d) => wrap(`${cab('HOJA DE INGRESO Y EGRESO DEL PACIENTE',d.fecha)}
    <div class="f2">${cv('Arch. Clínico / Expediente',p.expediente)}${cv('Tipo servicio clínico',d.tipo_servicio)}</div>
    ${cv('Nombre completo',p.paciente)}
    <div class="f4">${cv('Código Pte.',p.expediente)}${cv('Fecha de ingreso',d.fecha_ingreso)}${cv('Hora',d.hora_ingreso)}${cv('Sexo',p.sexo)}</div>
    <div class="f3">${cv('Edad',p.edad)}${cv('Fecha nacimiento',p.fecha_nac)}${cv('DUI',d.dui)}</div>
    ${cv('Aseguradora',p.seguro||d.aseguradora)}
    ${cv('Dirección',d.direccion||p.direccion)}
    <div class="f2">${cv('Tel. Casa',d.telefono_casa)}${cv('Otros teléfonos',d.otros_tel)}</div>
    <div class="f2">${cv('Ubicación / Habitación',p.habitacion)}${cv('Médico responsable',p.medico)}</div>
    <div class="f2"><div class="c"><span class="cl">Alergias:</span>${sino(d.alergias_sino)}</div><span class="cv">${d.alergias_detalle||''}</span></div>
    ${cv('Enfermedades crónicas',d.enf_cronicas)}
    ${cv('Diagnóstico de ingreso',d.diagnostico)}
    ${cv('Observaciones',d.observaciones)}
    <h3>Consentimiento y obligaciones</h3>
    <p style="font-size:11px;line-height:1.8;text-align:justify;margin-bottom:10px">Yo, <strong>${p.paciente}</strong>, voluntariamente y con pleno conocimiento, doy mi expreso consentimiento para que me practiquen los procedimientos médicos-quirúrgicos que los médicos que yo he escogido estimen convenientes, aceptando los riesgos, pues los beneficios superan a los mismos.</p>
    <p style="font-size:11px;line-height:1.8;text-align:justify;margin-bottom:10px">Yo, <strong>${d.nombre_responsable||'_______________________'}</strong>, me obligo al pago del monto de la cuenta que resulte por los Servicios Médicos, Hospitalarios y Otros, prestados al paciente <strong>${p.paciente}</strong>.</p>
    ${fir(d,'Firma del Paciente','Firma del Responsable')}
    <h3>Datos de egreso</h3>
    <div class="f3">${cv('Fecha de alta',d.fecha_egreso)}${cv('Hora',d.hora_egreso)}${cv('Estado de egreso',d.estado_egreso)}</div>
    ${cv('Diagnóstico principal de egreso',d.diagnostico_egreso)}
    ${cv('Procedimiento realizado',d.procedimiento_realizado)}
    ${cv('Medicamento de alta',d.medicamento_alta)}
    ${cv('Observaciones del egreso',d.obs_egreso)}
    ${fir(d,'Nombre y firma del Médico','Sello')}`),

  consentimiento: (p,d) => wrap(`${cab('CONSENTIMIENTO INFORMADO',d.fecha)}
    <p style="font-size:12px;line-height:1.9;text-align:justify;margin-bottom:14px">
    Yo, <strong>${p.paciente}</strong>, con DUI No. <strong>${d.dui||p.dui||'_________'}</strong>, de <strong>${p.edad||'__'}</strong> años de edad, de nacionalidad: <strong>${d.nacionalidad||p.nacionalidad||'Salvadoreño/a'}</strong>, paciente de ALTIMO CENTRO MÉDICO, quien me encuentro de forma voluntaria y en pleno uso de mis facultades mentales, doy mi consentimiento para que me administren el tratamiento médico quirúrgico necesario, y/o se practiquen los procedimientos de diagnóstico quirúrgicos que los médicos estimen convenientes. Entiendo que pueden presentarse complicaciones, sin embargo acepto los riesgos, pues los beneficios son superiores a los mismos.</p>
    ${cv('Médico tratante',p.medico)}
    ${cv('Procedimiento / Operación autorizada',d.procedimiento)}
    ${cv('Tratamiento indicado',d.tratamiento)}
    ${cv('Expediente No.',p.expediente)}
    ${fir(d,'Firma del paciente','Médico responsable y sello')}
    <h3>Datos del responsable (menores o imposibilitados de firmar)</h3>
    ${d.es_menor_imposibilitado === 'si' ? `
    <div class="f2">${cv('Nombre del responsable',d.nombre_responsable)}${cv('Edad',d.edad_responsable)}</div>
    ${cv('Parentesco',d.parentesco)}
    <div class="f2">${cv('Tipo de documento',d.tipo_doc_responsable)}${cv('No. de documento',d.num_doc_responsable)}</div>
    ${fir(d,'Firma del responsable','')}
    ` : `
    <p style="font-size:11px;color:#666;font-style:italic;padding:8px 0">
      N/A — El paciente firmó por sí mismo (no es menor de edad ni está imposibilitado).
    </p>
    `}
    <p style="font-size:10px;margin-top:10px;color:#555"><strong>NOTA:</strong> Junto a este formato debe anexarse fotocopias de DUI, pasaporte, tarjeta de identificación internacional o tarjeta de seguro privado.</p>`),

  ingreso_enfermera: (p,d) => wrap(`${cab('INGRESO DE ENFERMERÍA',d.fecha)}
    <div class="f3">${cv('Nombre',p.paciente)}${cv('Habitación',p.habitacion)}${cv('Médico',p.medico)}</div>
    <div class="f3">${cv('Ingresa por',d.motivo_ingreso)}${cv('Hora',d.hora_ingreso)}${cv('Fecha',d.fecha_ingreso)}</div>
    <h3>Antecedentes</h3>
    ${cv('Historia de enfermedades',d.historia_enfermedades)}
    ${cv('Tratamientos anteriores',d.tratamientos_anteriores)}
    <div class="f2"><div class="c"><span class="cl">Servicio anterior:</span>${sino(d.servicio_anterior)}</div><div class="c"><span class="cl">Alergias:</span>${sino(d.alergias_sino)}</div></div>
    <div class="f2">${cv('Especifique alergias',d.alergias_detalle)}${cv('Medicamentos especiales',d.medicamentos_especiales)}</div>
    <div class="f3">
      <div class="c"><span class="cl">Diabética/o:</span>${sino(d.diabetica)}</div>
      <div class="c"><span class="cl">Cardíaca/o:</span>${sino(d.cardiaca)}</div>
      <div class="c"><span class="cl">Otros padecimientos:</span>${sino(d.otros_padecimientos_sino)}
        ${d.otros_padecimientos_sino === 'si' ? `<span style="margin-left:6px;font-size:11px">${d.otros_padecimientos||''}</span>` : ''}
      </div>
    </div>
    <div class="f2"><div class="c"><span class="cl">Dr. da instrucciones tel:</span>${sino(d.instrucciones_tel)}</div><div class="c"><span class="cl">Instrucciones escritas:</span>${sino(d.instrucciones_escritas)}</div></div>
    <h3>Signos vitales al ingreso</h3>
    <div class="f4">${cv('Peso (lb)',d.peso)}${cv('Edad',p.edad)}${cv('T.A.',d.ta)}${cv('Pulso',d.pulso)}</div>
    <div class="f4">${cv('Temperatura °C',d.temperatura)}${cv('Respiración',d.resp)}${cv('Grupo sanguíneo',d.grupo_sanguineo)}${cv('Sat O2 %',d.sat_o2)}</div>
    <h3>Notas de enfermería</h3>
    <div class="bloque">${d.notas||''}</div>
    ${fir(d,'Nombre y firma de la Enfermera','')}`),

  pagare: (p,d) => wrap(`${cab('PAGARÉ "SIN PROTESTO"',d.fecha)}
    ${cv('Lugar y fecha',d.lugar_fecha)}
    <p style="font-size:12px;line-height:1.9;text-align:justify;margin:14px 0">Por este PAGARÉ, sin protesto, en fecha <strong>${d.dia || fechaPagareEnLetras(d.fecha_pagare) || '______________________________'}</strong>, me/nos obligo/amos a pagar de forma incondicional a la orden de <strong>ALTIMO MEDICAL CENTER</strong>, la suma de <strong>$${d.monto||'_____'}</strong> (<strong>${d.monto_texto||'___________________'}</strong> dólares), más intereses del 10% mensual calculados a partir de la fecha de suscitación, por servicios prestados al paciente <strong>${p.paciente}</strong>, Expediente <strong>${p.expediente}</strong>.</p>
    <div class="f3">${cv('No. de cuotas',d.cuotas)}${cv('Monto por cuota ($)',d.monto_cuota)}${cv('Fecha de vencimiento',d.fecha_vencimiento)}</div>
    ${cv('Nombre del suscriptor',d.nombre_deudor)}${cv('DUI del suscriptor',d.dui_deudor)}
    ${fir(d,'Firma del suscriptor','')}
    <h3>Garantía</h3>
    <div class="f2">${cv('Nombre firmante 1',d.nombre_firmante1)}${cv('DUI',d.dui_firmante1)}</div>
    <div class="f2">${cv('Nombre firmante 2',d.nombre_firmante2)}${cv('DUI',d.dui_firmante2)}</div>
    ${fir(d,'Firma firmante 1','Firma firmante 2','Administración ALTIMO')}`),

  evolucion: (p,d) => wrap(`${cab('HOJA DE EVOLUCIÓN MÉDICA',d.fecha)}
    <div class="f3">${cv('Nombre',p.paciente)}${cv('Edad',p.edad)}${cv('Expediente',p.expediente)}</div>
    ${cv('Médico',p.medico)}${cv('Diagnóstico',d.diagnostico)}
    ${d.filas_evolucion && d.filas_evolucion.length > 0
      ? tbl(['Fecha y hora','Médico que anota','Evolución / Indicaciones médicas','Firma'], d.filas_evolucion)
      : tbl(['Fecha y hora','Médico que anota','Evolución / Indicaciones médicas','Firma'], rows(15,[' ',' ',' ',' ']))
    }`),

  ordenes_medico: (p,d) => wrap(`${cab('ÓRDENES DEL MÉDICO Y CUMPLIMIENTO DE MEDICAMENTOS',d.fecha)}
    <div class="f3"><div style="display:flex;gap:4px">${cv('Nombre',p.paciente)}</div>${cv('Edad',p.edad)}${cv('Expediente',p.expediente)}</div>
    <h3>Indicaciones y prescripciones del médico</h3>
    <p style="font-size:10px;color:#888;margin-bottom:4px">⚠ Fecha y hora registradas automáticamente por el sistema — no manipulables.</p>
    ${d.filas_ordenes && d.filas_ordenes.length > 0
      ? tbl(['Fecha y hora','Médico','Indicaciones / Prescripciones'], d.filas_ordenes)
      : tbl(['Fecha y hora','Médico','Indicaciones / Prescripciones'], rows(10,[' ',' ',' ']))
    }
    <h3 style="margin-top:16px">Registro de cumplimiento</h3>
    <p style="font-size:10px;color:#888;margin-bottom:4px">⚠ Fecha/hora de cumplimiento registradas automáticamente por el sistema.</p>
    ${d.filas_cumplimiento && d.filas_cumplimiento.length > 0
      ? tbl(['Fecha/Hora cumplimiento','Descripción de la prescripción','Cumplido por (enfermera)'], d.filas_cumplimiento)
      : tbl(['Fecha/Hora cumplimiento','Descripción de la prescripción','Cumplido por (enfermera)'], rows(10,[' ',' ',' ']))
    }`),

  control_medicos: (p,d) => wrap(`${cab('CONTROL DE MÉDICOS TRATANTES E INTERCONSULTAS',d.fecha)}
    <div class="f2">${cv('Paciente',p.paciente)}${cv('Expediente',p.expediente)}</div>
    ${tbl(['N°','Nombre del médico','Especialidad','Teléfonos'],Array(10).fill(null).map((_,i)=>[i+1,' ',' ',' ']))}
    ${cv('Observaciones',d.observaciones)}
    <p style="font-size:10px;margin-top:10px;color:#555"><strong>NOTA:</strong> Para cobro de honorarios médicos</p>`),

  chequeo_quirurgico: (p,d) => wrap(`${cab('LISTA DE CHEQUEO PREVIO A PROCEDIMIENTO MÉDICO / QUIRÚRGICO',d.fecha)}
    <div class="f2">${cv('Paciente',p.paciente)}${cv('Expediente',p.expediente)}</div>
    <div class="f2">${cv('Cirugía a efectuar',d.cirugia)}${cv('Horas de ayuno',d.horas_ayuno)}</div>
    <div class="f2">${cv('Cirujano',p.medico)}${cv('Anestesiólogo',d.anestesiologo)}</div>
    ${d.fecha_hora_llenado ? `<div style="background:#e8f0fb;border:1px solid #c8ddf7;border-radius:6px;padding:7px 14px;font-size:11px;font-weight:700;color:#1B2A5E;margin:6px 0 10px;display:inline-block">📋 Fecha y hora de llenado: <span style="color:#1D9E75">${d.fecha_hora_llenado}</span> — Este registro queda bloqueado y no puede modificarse.</div>` : ''}
    <h3>Antecedentes y tratamientos del paciente</h3>
    <table><tbody>
    ${[
      ['Alergias','alergias'],['Hipertensión Arterial','hta'],['Diabetes Mellitus','dm'],
      ['Enfermedades Cardíacas','cardiacas'],['Enfermedades Neurológicas','neurologicas'],
      ['Cirugías Previas','cirugias_previas'],['Toma medicamentos a diario','medicamentos_diario'],
      ['Prótesis o implantes dentales removibles','protesis'],
      ['Acepta transfusión de derivados sanguíneos','transfusion'],
      ['Fuma','fuma'],['Ingesta de Alcohol','alcohol'],
      ['Usa lentes de contacto','lentes'],['Marcapasos','marcapasos'],
    ].map(([label,key])=>`<tr style="background:${['alergias','hta','dm','cardiacas','neurologicas'].includes(key)?'#fff8e1':'#fff'}">
      <td style="padding:5px 8px;font-weight:600;font-size:11px;width:55%">${label}</td>
      <td style="padding:5px 8px;font-size:12px">${sino(d[key])}</td>
      <td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee">${d[key+'_detalle']||''}</td>
    </tr>`).join('')}
    </tbody></table>
    <div class="f2">${cv('Trae evaluaciones (especialidad)',d.evaluaciones)}${cv('Especifique exámenes',d.examenes_detalle)}</div>
    <div class="f2"><div class="c"><span class="cl">Trae placas Rx:</span>${sino(d.rx)}</div><div class="c"><span class="cl">Trae exámenes:</span>${sino(d.examenes)}</div></div>
    ${cv('Indicaciones preoperatorias',d.indicaciones_preop)}
    <h3>Lista de verificación quirúrgica</h3>
    <table><thead><tr><th>Ítem</th><th style="width:80px;text-align:center">Sí / No</th><th>Observación</th></tr></thead><tbody>
    ${[
      ['Consentimiento informado firmado','chk_consentimiento'],
      ['Exámenes de laboratorio listos','chk_lab'],
      ['Ayuno confirmado','chk_ayuno'],
      ['Alergias verificadas','chk_alergias'],
      ['Medicamentos preoperatorios administrados','chk_medicamentos'],
      ['Sitio quirúrgico marcado','chk_sitio'],
      ['Historia clínica completa','chk_historia'],
      ['Grupo sanguíneo verificado','chk_grupo'],
      ['Vía IV funcional','chk_via_iv'],
      ['Prótesis y metales retirados','chk_protesis'],
      ['Resultados de imagen disponibles','chk_imagen'],
      ['Anestesiólogo notificado','chk_anestesiologo'],
      ['Región operatoria preparada','chk_region'],
      ['Reporte de exámenes revisado','chk_reporte'],
    ].map((item,i)=>`<tr style="background:${i%2===0?'#f8fafd':'#fff'}"><td style="padding:5px 8px;font-size:11px">${item[0]}</td><td style="text-align:center;font-size:13px">${sino(d[item[1]])}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee">${d[item[1]+'_obs']||''}</td></tr>`).join('')}
    </tbody></table>
    ${d.notas_generales?`<p style="margin-top:10px;font-size:11px"><strong>Notas:</strong> ${d.notas_generales}</p>`:''}
    ${fir(d,'Enfermera circulante','Anestesiólogo','Firma y sello del Médico')}
    <p style="font-size:9px;color:#888;margin-top:14px;border-top:1px solid #eee;padding-top:6px">
      ⚠ Documento de seguridad clínica — una vez guardado queda bloqueado para garantizar trazabilidad.
      ${d.fecha_hora_llenado ? `Llenado el: ${d.fecha_hora_llenado}.` : ''}
    </p>`),

  signos_vitales: (p,d) => wrap(`${cab('HOJA DE SIGNOS VITALES',d.fecha)}
    <div class="f2">${cv('Nombre',p.paciente)}${cv('Edad',p.edad)}</div>
    <div class="f2">${cv('Médico',p.medico)}${cv('Expediente #',p.expediente)}</div>
    ${tbl(['Fecha','Hora','T°C','T.A.','Pulso','Resp.','Sat O2','Orina ml','Observaciones','Firma'],
      (d.filas_sv||rows(14,[' ',' ',' ',' ',' ',' ',' ',' ',' ',' ']))
    )}`),

  reporte_enfermera: (p,d) => wrap(`${cab('REPORTE DE ENFERMERÍA',d.fecha)}
    <div class="f3">${cv('Nombre',p.paciente)}${cv('Expediente',p.expediente)}${cv('Habitación',p.habitacion)}</div>
    <div class="f3">${cv('Edad',p.edad)}${cv('Médico',p.medico)}${cv('Turno',d.turno)}</div>
    ${cv('Enfermera responsable',d.enfermera)}
    <h3>Evolución / Consultas / Condición del paciente</h3>
    <p style="font-size:10px;color:#888;margin-bottom:4px">Total de notas registradas: ${(d.filas_rpt||[]).length}</p>
    ${d.filas_rpt && d.filas_rpt.length > 0
      ? tbl(['Fecha y hora','Notas clínicas / Evolución','Firma'], d.filas_rpt)
      : tbl(['Fecha y hora','Notas clínicas / Evolución','Firma'], rows(12,[' ',' ',' ']))
    }`),

  temp_pulso: (p,d) => wrap(`${cab('HOJA DE TEMPERATURA Y PULSO',d.fecha)}
    <div class="f3">${cv('Nombre',p.paciente)}${cv('Expediente',p.expediente)}${cv('Edad',p.edad)}</div>
    ${cv('Mes',d.mes)}
    ${tbl(['Día','Hora AM','Temp AM °C','Pulso AM','Hora PM','Temp PM °C','Pulso PM','Resp.','Observaciones'],
      Array(16).fill(null).map((_,i)=>[i+1,' ',' ',' ',' ',' ',' ',' ',' '])
    )}`),

  balance_hidrico: (p,d) => wrap(`${cab('HOJA CONTROL BALANCE HÍDRICO',d.fecha)}
    <div class="f3">${cv('Nombre',p.paciente)}${cv('Expediente',p.expediente)}${cv('No. Cama',d.cama)}</div>
    <div class="f3">${cv('Servicio',d.servicio)}${cv('Edad',p.edad)}${cv('Sexo',p.sexo)}</div>
    <div class="f2">${cv('Médico',p.medico)}${cv('Fecha',d.fecha_registro)}</div>
    <h3>INGRESOS</h3>
    ${tbl(['Hora','Oral ml','Sueros','Med. Parenterales','Transfusión','Plasma','Otros','Firma'],
      (d.filas_ingresos||[
        ...['7am','8am','9am','10am','11am','12m','1pm','2pm'].map(h=>[h,...Array(7).fill(' ')]),
        ['Total',...Array(7).fill(' ')],
        ...['3pm','4pm','5pm','6pm','7pm','8pm','9pm','10pm'].map(h=>[h,...Array(7).fill(' ')]),
        ['Total',...Array(7).fill(' ')],
        ...['11pm','12m','1am','2am','3am','4am','5am','6am'].map(h=>[h,...Array(7).fill(' ')]),
        ['Total',...Array(7).fill(' ')],
      ])
    )}
    <h3>ELIMINACIONES</h3>
    ${tbl(['Hora','Vómitos','Orina','Evacuaciones','Drenajes','Aspiraciones','Firma'],
      (d.filas_elim||[...Array(8).fill(null).map(()=>Array(7).fill(' ')),['Total',...Array(6).fill(' ')]])
    )}
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-top:8px;font-weight:700;font-size:12px">
      <div>Total Ingresos: ${d.total_ingresos||'___'} ml</div>
      <div>Total Eliminaciones: ${d.total_egresos||'___'} ml</div>
      <div style="background:#e8f0fb;padding:6px;border-radius:6px">Balance 24h: ${d.balance||'___'} ml</div>
    </div>
    ${fir(d,'Enfermera responsable','Médico responsable')}`),

  tarjeta_medicamento: (p,d) => wrap(`${cab('CUIDADOS DE ENFERMERÍA – TRATAMIENTO Y EXÁMENES',d.fecha)}
    <div class="f2">${cv('Paciente',p.paciente)}${cv('Habitación',p.habitacion)}</div>
    <div class="f2">${cv('Diagnóstico',d.diagnostico)}${cv('Edad',p.edad)}</div>
    <div class="f2">${cv('Alérgico a',d.alergias)}${cv('Expediente',p.expediente)}</div>
    <div class="f2">${cv('Médico',p.medico)}${cv('Seguro',p.seguro)}</div>
    <h3>Medicamentos y tratamiento</h3>
    ${tbl(['Fecha','Cuidados','Exámenes','Tratamiento','7am-3pm','3pm-10pm','10pm-7am','Observaciones'],
      (d.filas_med||rows(10,[' ',' ',' ',' ',' ',' ',' ',' ']))
    )}`),

  tarjeta_oral: (p,d) => wrap(`${cab('TARJETA VÍA ORAL',d.fecha)}
    <div class="f2">${cv('Paciente',p.paciente)}${cv('Edad',p.edad)}</div>
    <div class="f2">${cv('Diagnóstico',d.diagnostico)}${cv('Fecha inicio',d.fecha_inicio)}</div>
    <div class="f2">${cv('Alérgico a',d.alergias)}${cv('Expediente',p.expediente)}</div>
    <div class="f2">${cv('Médico',p.medico)}${cv('Empresa / Seguro',p.seguro)}</div>
    ${tbl(['Medicamento','Dosis','Freq.','Días','6am','12pm','6pm','10pm','Obs.'],
      (d.filas_oral||rows(10,[' ',' ',' ',' ',' ',' ',' ',' ',' ']))
    )}`),

  tarjeta_parenteral: (p,d) => wrap(`${cab('TARJETA PARENTERAL',d.fecha)}
    <div class="f2">${cv('Paciente',p.paciente)}${cv('Edad',p.edad)}</div>
    <div class="f2">${cv('Diagnóstico',d.diagnostico)}${cv('Fecha inicio',d.fecha_inicio)}</div>
    <div class="f2">${cv('Alérgico a',d.alergias)}${cv('Expediente',p.expediente)}</div>
    <div class="f2">${cv('Médico',p.medico)}${cv('Empresa / Seguro',p.seguro)}</div>
    ${tbl(['Medicamento / Solución','Vía','Dosis','7am-3pm','3pm-10pm','10pm-7am'],
      (d.filas_par||rows(10,[' ',' ',' ',' ',' ',' ']))
    )}`),

  prog_cirugias: (p,d) => {
    const coloresQ = ['#993C1D','#185FA5','#1D9E75','#5F5E5A','#534AB7','#854F0B'];
    const COLS_DISPLAY = ['Hora','Cirujano','Paciente','Nombre de la Cirugía','Anestesiólogo','Otros','Fecha y Hora Anotación','Responsable','Observaciones','Estado'];
    const ESTADO_BG = { PROGRAMADA:'#dbeafe', REPROGRAMADA:'#fff3e0', REALIZADA:'#dcfce7' };
    const ESTADO_ICON = { PROGRAMADA:'📋', REPROGRAMADA:'🔄', REALIZADA:'✅' };
    const normR = (row) => {
      const r = Array.isArray(row) ? [...row] : [row];
      while(r.length < 11) r.push('');
      return r;
    };
    const tabCx = (key, label, colorIdx) => {
      const color  = coloresQ[colorIdx % coloresQ.length];
      const raw    = d[key];
      const filas  = (Array.isArray(raw) && raw.length === 15) ? raw.map(normR) : HORAS_CX.map(h=>[h,'','','','','','','','','','[]']);
      return `<div style="margin-bottom:0;font-weight:800;font-size:11px;color:#fff;background:${color};padding:6px 10px;border-radius:4px 4px 0 0">${label}</div>
      <table style="margin-top:0;font-size:10px;border:1px solid ${color};margin-bottom:8px">
        <thead><tr>
          ${COLS_DISPLAY.map(c=>`<th style="padding:4px 5px;font-size:9px;white-space:nowrap;background:${color}dd;color:#fff;min-width:${c==='Observaciones'?'140px':c==='Nombre de la Cirugía'?'110px':c==='Fecha y Hora Anotación'?'90px':c==='Estado'?'75px':'58px'}">${c}</th>`).join('')}
        </tr></thead>
        <tbody>${filas.map((row,i)=>{
          const estado = String(row[9]||'');
          const rowBg  = ESTADO_BG[estado] || (i%2===0?'#fff':'#f8fafd');
          const borderL= estado ? `border-left:3px solid ${coloresQ[Object.keys(ESTADO_BG).indexOf(estado)]||'#ccc'}` : '';
          return `<tr style="background:${rowBg};${borderL}">
            ${row.slice(0,9).map((cel,ci)=>`<td style="padding:4px 5px;font-size:10px;${ci===0?'font-weight:700;background:#e8f0fb;white-space:nowrap;text-align:center;':''} ${ci===6?'background:#f0fff8;font-size:9px;color:#1D9E75;':''}${ci===8?'min-width:140px;':''}">
              ${cel||'&nbsp;'}
            </td>`).join('')}
            <td style="padding:4px 5px;font-size:9px;font-weight:700;text-align:center;white-space:nowrap">
              ${estado ? (ESTADO_ICON[estado]||'')+'&nbsp;'+estado : '—'}
            </td>
          </tr>`;
        }).join('')}
        </tbody>
      </table>`;
    };
    const tablasHtml = QUIROFANOS_CONFIG.map((q, idx) =>
      tabCx('filas_'+q.id, q.label.toUpperCase(), idx)
    ).join('');
    return wrap(`${cab('PROGRAMACIÓN DE CIRUGÍAS',d.fecha)}
      ${cv('Fecha de cirugías',d.fecha_cirugia)}
      ${tablasHtml}
      <p style="font-size:9px;color:#888;margin-top:6px;border-top:1px solid #eee;padding-top:5px">
        ⚠ Fecha y Hora Anotación: registrada automáticamente — no modificable.
        Solo Observaciones puede modificarse tras guardar. Datos en MAYÚSCULAS.
        Estados: 📋 Programada · 🔄 Reprogramada · ✅ Realizada
      </p>`);
  },

  gastos_sala: (p,d) => wrap(`${cab('HOJA DE GASTOS SALA DE OPERACIÓN',d.fecha)}
    <div class="f3">${cv('Paciente',p.paciente)}${cv('Expediente',p.expediente)}${cv('Edad',p.edad)}</div>
    <div class="f3">${cv('Fecha',d.fecha_cirugia)}${cv('Quirófano No.',d.quirofano)}${cv('Habitación',p.habitacion)}</div>
    <div class="f2">${cv('Cirugía realizada',d.cirugia_realizada)}${cv('Seguro / Particular',p.seguro||d.particular)}</div>
    <div class="f2">${cv('Cirujano',p.medico)}${cv('Ayudante',d.ayudante)}</div>
    <div class="f3">${cv('Anestesiólogo',d.anestesiologo)}${cv('Tipo de anestesia',d.tipo_anestesia)}${cv('Instrumentista',d.instrumentista)}</div>
    <div class="f3">${cv('Hora inicio Cx',d.hora_inicio)}${cv('Hora final Cx',d.hora_final)}${cv('Tiempo de sala',d.tiempo_sala)}</div>
    <h3>Insumos y materiales utilizados</h3>
    ${tbl(['Cantidad','Descripción del insumo / material','Observación'],
      (d.insumos_gastos||rows(20,[' ',' ',' ']))
    )}
    ${cv('Observaciones generales',d.observaciones)}
    ${fir(d,'Enfermera instrumentista','Enfermera circulante','Cirujano y sello')}`),

  reporte_anestesia: (p,d) => wrap(`${cab('REPORTE DE ANESTESIA EN QUIRÓFANO',d.fecha)}
    <div class="f3">${cv('Paciente',p.paciente)}${cv('Código / Exp.',p.expediente)}${cv('Fecha',d.fecha)}</div>
    <div class="f3">${cv('Anestesiólogo',d.anestesiologo)}${cv('Peso (kg)',d.peso)}${cv('Talla (cm)',d.talla)}</div>
    <div class="f2">${cv('Hora inicio anestesia',d.hora_inicio)}${cv('Hora fin anestesia',d.hora_fin)}</div>
    <div style="display:flex;gap:16px;margin:8px 0;font-size:12px">
      <strong>Tipo de anestesia:</strong>
      ${['General','Raquídea','Sedación','Local'].map(t=>`<span>${d.tipo_anestesia===t?'☑':'☐'} ${t}</span>`).join(' ')}
    </div>
    <h3>Medicamentos utilizados</h3>
    ${tbl(['Medicamento','Cantidad','Medicamento','Cantidad'],
      (d.medicamentos_anest||[
        ['Sevorane','','Electrodos para monitoreo',''],['Isoflorane','','Set catéter epidural',''],
        ['Remifentanil','','Aguja punción lumbar',''],['Fentanil','','Tubo endotraqueal',''],
        ['Morfina','','Máscara laríngea',''],['Pethidine','','Sonda nasogástrica',''],
        ['Tramal','','Solución salina normal',''],['Ketamina','','Hartmann',''],
        ['Propofol','','Oxígeno',''],['Etomidato','','Otros',''],
        ['Midazolam','','',''],['Atropina','','',''],
        ['Neostigmina','','',''],['Ketorolaco','','',''],
        ['Dexametasona','','',''],['Metoclopramida','','',''],
        ['Ondansetron','','',''],['Ranitidina','','',''],
      ])
    )}
    ${cv('Notas / Complicaciones',d.notas)}
    ${fir(d,'Firma del Anestesiólogo','')}`),

  reporte_operatorio: (p,d) => wrap(`${cab('REPORTE OPERATORIO',d.fecha)}
    <div class="f3">${cv('Paciente',p.paciente)}${cv('Habitación',p.habitacion)}${cv('Código',p.expediente)}</div>
    <div class="f3">${cv('Edad',p.edad)}${cv('Sexo',p.sexo)}${cv('Fecha de operación',d.fecha_operacion)}</div>
    <div class="f2">${cv('Cirujano',p.medico)}${cv('Ayudante',d.ayudante)}</div>
    <div class="f2">${cv('2do. Ayudante',d.ayudante2)}${cv('Anestesiólogo',d.anestesiologo)}</div>
    <div class="f3">${cv('Hora inicio',d.hora_inicio)}${cv('Hora fin',d.hora_final)}${cv('Tiempo operatorio',d.tiempo_op)}</div>
    <div class="f2">${cv('Enf. circular',d.enf_circular)}${cv('Enf. instrumentista',d.enf_instrumentista)}</div>
    ${cv('Operación practicada',d.operacion_practicada)}
    ${cv('Diagnóstico pre-operatorio',d.diag_pre)}
    ${cv('Diagnóstico post-operatorio',d.diag_post)}
    ${cv('Drenajes',d.drenajes)}<div class="f2">${cv('Biopsia',d.biopsia)}${cv('Laboratorio',d.lab_intra)}</div>
    <h3>Descripción de la técnica quirúrgica</h3>
    <div class="bloque" style="min-height:200px">${d.descripcion_tecnica||''}</div>
    <h3>Esquema</h3>
    <div style="border:1px solid #ccc;height:120px;border-radius:4px"></div>
    ${fir(d,'Firma y sello del Cirujano','')}`),

  honorarios: (p,d) => wrap(`${cab('QUEDAN HONORARIOS MÉDICOS',d.fecha)}
    <div class="f2">${cv('Correlativo No.',d.correlativo)}${cv('Valor $',d.monto)}</div>
    <p style="font-size:12px;margin:10px 0">Se recibe documento: <strong>${d.tipo_doc||'___'}</strong> No. <strong>${d.num_doc||'___'}</strong> de fecha <strong>${d.fecha_doc||'___'}</strong></p>
    <p style="font-size:12px">Para gestionar el cobro de honorarios por servicios profesionales en <strong>ALTIMO CENTRO MÉDICO</strong>.</p>
    ${cv('Por el Doctor/a',p.medico||d.doctor)}
    ${cv('Al paciente',p.paciente)}
    ${cv('Con código No.',p.expediente)}
    <p style="margin-top:12px;font-size:12px">San Salvador, ${new Date().toLocaleDateString('es-SV')}</p>
    ${fir(d,'Original','Copia')}`),

  asignaciones: (p,d) => wrap(`${cab('ASIGNACIONES ALTIMO CENTRO MÉDICO',d.fecha)}
    ${tbl(['Nombres','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'],
      rows(15,[' ',' ',' ',' ',' ',' ',' ',' '])
    )}`),

  reporte_insumos: (p,d) => wrap(`${cab('REPORTE DE INSUMOS UTILIZADOS',d.fecha)}
    <div class="f2">${cv('Paciente',p.paciente)}${cv('Expediente',p.expediente)}</div>
    <div class="f3">${cv('Fecha',d.fecha)}${cv('No. habitación',p.habitacion)}${cv('Hora',d.hora)}</div>
    <div style="font-size:11px;margin-bottom:4px;font-weight:700">Correlativo: ${d.correlativo||'___'}</div>
    ${tbl(['Cantidad','Presentación','Descripción del producto'],
      (d.insumos_rows||rows(15,[' ',' ',' ']))
    )}
    ${fir(d,'Enfermera de turno','Solicitante','Recibido')}`),

  req_productos: (p,d) => wrap(`${cab('REQUISICIÓN DE PRODUCTOS MÉDICOS Y DESCARTABLES',d.fecha)}
    <div class="f2">${cv('Paciente',p.paciente)}${cv('Expediente',p.expediente)}</div>
    <div class="f3">${cv('Fecha',d.fecha)}${cv('No. habitación',p.habitacion)}${cv('Hora',d.hora)}</div>
    <div style="font-size:11px;margin-bottom:4px;font-weight:700">Correlativo: ${d.correlativo||'___'}</div>
    ${tbl(['Cantidad','Presentación','Descripción del producto'],
      (d.productos_rows||rows(15,[' ',' ',' ']))
    )}
    ${fir(d,'Enfermera de turno','Solicitante','Recibido')}`),

  control_temp_refri: (p,d) => wrap(`${cab('CONTROL DE TEMPERATURA Y LAVADO DE REFRIGERADORA PARA MEDICAMENTOS',d.fecha)}
    <p style="font-size:11px;margin-bottom:8px;color:#666">Mantener temperatura de 2°C a 8°C. Lavado de refrigeradora los días Martes.</p>
    ${tbl(['Fecha','Hora','Temperatura encontrada (°C)','Firma y sello de quien verificó'],
      (d.filas_temp||rows(20,[' ',' ',' ',' ']))
    )}`),

  lista_asistencia: (p,d) => wrap(`${cab('LISTA DE ASISTENCIA',d.fecha)}
    <div class="f2">${cv('Nombre de la capacitación',d.nombre_capacitacion)}${cv('Fecha',d.fecha_evento)}</div>
    <div class="f2">${cv('Impartido por',d.impartido_por)}${cv('Lugar',d.lugar)}</div>
    ${tbl(['N°','Nombre completo','Cargo','Firma'],
      Array(20).fill(null).map((_,i)=>[i+1,' ',' ',' '])
    )}`),

  acciones_personal: (p,d) => wrap(`${cab('ACCIONES DE PERSONAL',d.fecha)}
    <div class="f2">${cv('Nombre del empleado',d.nombre_empleado)}${cv('Área donde labora',d.area_labora)}</div>
    ${cv('Cargo',d.cargo)}
    <h3>Tipo de acción</h3>
    <table><thead><tr><th>Acciones administrativas</th><th>Acciones positivas</th><th>Acciones negativas</th></tr></thead><tbody>
    <tr>
      <td style="font-size:11px">${['Permiso','Traslado','Contratación','Vacación','Renuncia','Licencia con goce','Licencia sin goce','Otros'].map((a,i)=>`${i+1}. ${a}`).join('<br>')}</td>
      <td style="font-size:11px">${['Felicitación','Preparación académica','Capacitación','Promociones'].map((a,i)=>`${i+9}. ${a}`).join('<br>')}</td>
      <td style="font-size:11px">${['Suspensión','Descuento','Amonestación','Destitución','Amonestación verbal','Observación'].map((a,i)=>`${i+13}. ${a}`).join('<br>')}</td>
    </tr></tbody></table>
    <h3>Detalle de la acción</h3>
    ${tbl(['Fecha','N° de acción','Explicación de causas, tiempo de duración u otras circunstancias','Firma jefe inmediato'],
      rows(5,[' ',' ',' ',' '])
    )}`),
};

// ════════════════════════════════════════════════════════════
//  CAMPOS DEL EDITOR (por formulario)
// ════════════════════════════════════════════════════════════
const CAMPOS = {
  ingreso_egreso: [
    { sec:'Datos del paciente', items:[
      { id:'tipo_servicio', label:'Tipo de servicio clínico', tipo:'select', ops:['Medicina General','Cirugía','Pediatría','Ginecología','Traumatología','UCI','Maternidad','Otro'], col:2 },
      { id:'dui',           label:'DUI del paciente', tipo:'text', col:2 },
      { id:'aseguradora',   label:'Aseguradora', tipo:'text', col:2 },
      { id:'direccion',     label:'Dirección completa', tipo:'text' },
      { id:'telefono_casa', label:'Tel. casa', tipo:'text', col:2 },
      { id:'otros_tel',     label:'Otros teléfonos', tipo:'text', col:2 },
    ]},
    { sec:'Alergias y diagnóstico', items:[
      { id:'alergias_sino',   label:'¿Tiene alergias?', tipo:'siNo' },
      { id:'alergias_detalle',label:'Especifique las alergias', tipo:'text' },
      { id:'enf_cronicas',    label:'Enfermedades crónicas', tipo:'text' },
      { id:'diagnostico',     label:'Diagnóstico de ingreso', tipo:'text' },
      { id:'observaciones',   label:'Observaciones', tipo:'textarea' },
    ]},
    { sec:'Responsable de la cuenta', items:[
      { id:'nombre_responsable', label:'Nombre del responsable financiero', tipo:'text' },
    ]},
    { sec:'Ingreso', items:[
      { id:'fecha_ingreso', label:'Fecha de ingreso', tipo:'date', col:3 },
      { id:'hora_ingreso',  label:'Hora de ingreso',  tipo:'time', col:3 },
    ]},
    { sec:'Datos de egreso', items:[
      { id:'fecha_egreso',         label:'Fecha de alta',       tipo:'date', col:3 },
      { id:'hora_egreso',          label:'Hora de alta',        tipo:'time', col:3 },
      { id:'estado_egreso',        label:'Estado de egreso',    tipo:'select', ops:['Mejorado','Curado','Traslado','Defunción','Voluntaria','Otro'], col:3 },
      { id:'diagnostico_egreso',   label:'Diagnóstico principal de egreso', tipo:'text' },
      { id:'procedimiento_realizado', label:'Procedimiento realizado', tipo:'text' },
      { id:'medicamento_alta',     label:'Medicamento de alta', tipo:'text' },
      { id:'obs_egreso',           label:'Observaciones del egreso', tipo:'textarea' },
    ]},
  ],

  consentimiento: [
    { sec:'Datos del procedimiento', items:[
      { id:'dui',           label:'DUI del paciente', tipo:'text', col:2 },
      { id:'nacionalidad',  label:'Nacionalidad',     tipo:'text', col:2 },
      { id:'procedimiento', label:'Procedimiento / Operación autorizada', tipo:'text' },
      { id:'tratamiento',   label:'Tratamiento indicado', tipo:'text' },
    ]},
    { sec:'Datos del responsable (menores o imposibilitados para firmar)', items:[
      { id:'es_menor_imposibilitado', label:'¿El paciente es menor de edad o está imposibilitado para firmar?', tipo:'siNo' },
      { id:'nombre_responsable',  label:'Nombre del responsable', tipo:'text', col:2 },
      { id:'edad_responsable',    label:'Edad del responsable',   tipo:'text', col:2 },
      { id:'parentesco',          label:'Parentesco',             tipo:'select', ops:['Madre','Padre','Hijo/a','Cónyuge','Hermano/a','Tutor legal','Otro'] },
      { id:'tipo_doc_responsable',label:'Tipo de documento',      tipo:'select', ops:['DUI','Pasaporte','Partida de nacimiento','Otro'] },
      { id:'num_doc_responsable', label:'No. de documento',       tipo:'text' },
    ]},
  ],

  ingreso_enfermera: [
    { sec:'Datos de ingreso', items:[
      { id:'fecha_ingreso',  label:'Fecha de ingreso', tipo:'date', col:3 },
      { id:'hora_ingreso',   label:'Hora de ingreso',  tipo:'time', col:3 },
      { id:'motivo_ingreso', label:'Ingresa por / Motivo (precargado de admisión)', tipo:'text', readOnlyAuto:true },
    ]},
    { sec:'Antecedentes', items:[
      { id:'historia_enfermedades',   label:'Historia de enfermedades', tipo:'text' },
      { id:'tratamientos_anteriores', label:'Tratamientos anteriores',  tipo:'text' },
      { id:'servicio_anterior',       label:'¿Se le prestó servicio anteriormente?', tipo:'siNo' },
      { id:'alergias_sino',           label:'¿Tiene alergias?',         tipo:'siNo' },
      { id:'alergias_detalle',        label:'Especifique alergias',     tipo:'text' },
      { id:'medicamentos_especiales', label:'Medicamentos especiales',  tipo:'text' },
      { id:'diabetica',               label:'¿Diabética/o?',            tipo:'siNo', col:3 },
      { id:'cardiaca',                label:'¿Cardíaca/o?',             tipo:'siNo', col:3 },
      { id:'otros_padecimientos',     label:'¿Otros padecimientos?',    tipo:'sinoTexto', col:3 },
      { id:'instrucciones_tel',       label:'¿Doctor habla por tel. y da instrucciones?', tipo:'siNo', col:2 },
      { id:'instrucciones_escritas',  label:'¿Instrucciones escritas por médico?',         tipo:'siNo', col:2 },
    ]},
    { sec:'Signos vitales al ingreso', items:[
      { id:'peso',          label:'Peso (lb)',          tipo:'text', col:3 },
      { id:'ta',            label:'Tensión arterial',   tipo:'text', col:3 },
      { id:'pulso',         label:'Pulso',              tipo:'text', col:3 },
      { id:'temperatura',   label:'Temperatura °C',     tipo:'text', col:3 },
      { id:'resp',          label:'Respiración',        tipo:'text', col:3 },
      { id:'sat_o2',        label:'Sat. O2 %',          tipo:'text', col:3 },
      { id:'grupo_sanguineo', label:'Grupo sanguíneo',  tipo:'text', col:3 },
    ]},
    { sec:'Notas de enfermería', items:[
      { id:'notas', label:'Notas / Observaciones', tipo:'textarea', rows:6 },
    ]},
  ],

  pagare: [
    { sec:'Datos del pagaré', items:[
      { id:'lugar_fecha',      label:'Lugar y fecha',       tipo:'text' },
      { id:'fecha_pagare',     label:'Fecha del pagaré',    tipo:'date', col:2 },
      { id:'dia',              label:'Fecha en letras',     tipo:'text', col:2, readOnlyAuto:true },
      { id:'monto',            label:'Monto ($)',           tipo:'text', col:2 },
      { id:'monto_texto',      label:'Monto en letras',     tipo:'text', col:2, readOnlyAuto:true },
      { id:'cuotas',           label:'No. de cuotas',       tipo:'cuotasPagare', col:3 },
      { id:'monto_cuota',      label:'Monto por cuota ($)', tipo:'text', col:3, readOnlyAuto:true },
      { id:'fecha_vencimiento',label:'Fecha de vencimiento', tipo:'date', col:3 },
    ]},
    { sec:'Suscriptor', items:[
      { id:'nombre_deudor', label:'Nombre del suscriptor', tipo:'text', col:2 },
      { id:'dui_deudor',    label:'DUI del suscriptor',    tipo:'text', col:2 },
    ]},
    { sec:'Garantía', items:[
      { id:'nombre_firmante1', label:'Nombre firmante 1', tipo:'text', col:2 },
      { id:'dui_firmante1',    label:'DUI firmante 1',    tipo:'text', col:2 },
      { id:'nombre_firmante2', label:'Nombre firmante 2', tipo:'text', col:2 },
      { id:'dui_firmante2',    label:'DUI firmante 2',    tipo:'text', col:2 },
    ]},
  ],

  evolucion: [
    { sec:'Datos', items:[
      { id:'diagnostico', label:'Diagnóstico principal', tipo:'text', col:2 },
    ]},
    { sec:'Evolución médica', items:[
      { id:'filas_evolucion', label:'Notas de evolución', tipo:'tabla_dinamica',
        cols:['Fecha y hora','Médico que anota','Evolución / Indicaciones médicas','Firma'],
        iniciales: 0, autoFechaHora: true, autoFirma: true },
    ]},
  ],

  ordenes_medico: [
    { sec:'Indicaciones y prescripciones del médico', items:[
      { id:'filas_ordenes', label:'Órdenes del médico', tipo:'tabla_dinamica',
        cols:['Fecha y hora','Médico','Indicaciones / Prescripciones'],
        iniciales: 0, autoFechaHora: true, autoFirma: true },
    ]},
    { sec:'Registro de cumplimiento de indicaciones', items:[
      { id:'filas_cumplimiento', label:'Eventos de cumplimiento por enfermería', tipo:'tabla_dinamica',
        cols:['Fecha/Hora cumplimiento','Descripción de la prescripción','Cumplido por (enfermera)'],
        iniciales: 0, autoFechaHora: true, autoFirma: false,
        prefillFromField:'filas_ordenes',
        prefillSourceCol:'Indicaciones / Prescripciones',
        prefillTargetCol:'Descripción de la prescripción',
        prefillLabel:'Orden médica para registrar cumplimiento',
        addLabel:'+ Nuevo cumplimiento',
        emptyLabel:'Sin cumplimientos registrados — selecciona una orden médica y haz clic en "+ Nuevo cumplimiento".',
        prefillHelp:'Una misma orden puede registrar múltiples cumplimientos durante el turno. La descripción se carga automáticamente y queda bloqueada para conservar la indicación original.' },
    ]},
  ],

  control_medicos: [
    { sec:'Datos', items:[
      { id:'observaciones', label:'Observaciones generales', tipo:'textarea' },
    ]},
  ],

  chequeo_quirurgico: [
    { sec:'Registro automático', items:[
      { id:'fecha_hora_llenado', label:'Fecha y hora de llenado', tipo:'text', readOnlyAuto:true },
    ]},
    { sec:'Datos del procedimiento', items:[
      { id:'cirugia',       label:'Cirugía a efectuar', tipo:'text' },
      { id:'anestesiologo', label:'Anestesiólogo',      tipo:'text', col:2 },
      { id:'horas_ayuno',   label:'Horas de ayuno',     tipo:'text', col:2 },
    ]},
    { sec:'Antecedentes del paciente (Sí / No)', items:[
      { id:'alergias',          label:'Alergias',                           tipo:'siNo', detalle:true },
      { id:'hta',               label:'Hipertensión Arterial',              tipo:'siNo', detalle:true },
      { id:'dm',                label:'Diabetes Mellitus',                  tipo:'siNo', detalle:true },
      { id:'cardiacas',         label:'Enfermedades Cardíacas',             tipo:'siNo', detalle:true },
      { id:'neurologicas',      label:'Enfermedades Neurológicas',          tipo:'siNo', detalle:true },
      { id:'cirugias_previas',  label:'Cirugías Previas',                   tipo:'siNo', detalle:true },
      { id:'medicamentos_diario',label:'Toma medicamentos a diario',        tipo:'siNo', detalle:true },
      { id:'protesis',          label:'Prótesis o implantes dentales removibles', tipo:'siNo' },
      { id:'transfusion',       label:'Acepta transfusión de derivados sanguíneos', tipo:'siNo', detalle:true },
      { id:'fuma',              label:'Fuma',                               tipo:'siNo' },
      { id:'alcohol',           label:'Ingesta de alcohol',                 tipo:'siNo' },
      { id:'lentes',            label:'Usa lentes de contacto',             tipo:'siNo' },
      { id:'marcapasos',        label:'Marcapasos',                         tipo:'siNo' },
    ]},
    { sec:'Documentos y verificaciones', items:[
      { id:'evaluaciones',      label:'Trae evaluaciones médicas (especialidad)', tipo:'text', col:2 },
      { id:'rx',                label:'Trae placas Rx',    tipo:'siNo', col:3 },
      { id:'examenes',          label:'Trae exámenes',    tipo:'siNo', col:3 },
      { id:'examenes_detalle',  label:'Especifique exámenes', tipo:'text' },
      { id:'indicaciones_preop',label:'Indicaciones preoperatorias', tipo:'textarea' },
    ]},
    { sec:'Lista de chequeo quirúrgico', items:[
      { id:'chk_consentimiento', label:'Consentimiento informado firmado',         tipo:'siNo', obs:true },
      { id:'chk_lab',            label:'Exámenes de laboratorio listos',          tipo:'siNo', obs:true },
      { id:'chk_ayuno',          label:'Ayuno confirmado',                        tipo:'siNo', obs:true },
      { id:'chk_alergias',       label:'Alergias verificadas',                    tipo:'siNo', obs:true },
      { id:'chk_medicamentos',   label:'Medicamentos preoperatorios administrados', tipo:'siNo', obs:true },
      { id:'chk_sitio',          label:'Sitio quirúrgico marcado',                tipo:'siNo', obs:true },
      { id:'chk_historia',       label:'Historia clínica completa',               tipo:'siNo', obs:true },
      { id:'chk_grupo',          label:'Grupo sanguíneo verificado',              tipo:'siNo', obs:true },
      { id:'chk_via_iv',         label:'Vía IV funcional',                        tipo:'siNo', obs:true },
      { id:'chk_protesis',       label:'Prótesis y metales retirados',            tipo:'siNo', obs:true },
      { id:'chk_imagen',         label:'Resultados de imagen disponibles',        tipo:'siNo', obs:true },
      { id:'chk_anestesiologo',  label:'Anestesiólogo notificado',               tipo:'siNo', obs:true },
      { id:'chk_region',         label:'Región operatoria preparada',             tipo:'siNo', obs:true },
      { id:'chk_reporte',        label:'Reporte de exámenes revisado',           tipo:'siNo', obs:true },
    ]},
    { sec:'Observaciones finales', items:[
      { id:'notas_generales', label:'Notas generales del chequeo', tipo:'textarea' },
    ]},
  ],

  signos_vitales: [
    { sec:'Datos', items:[
      { id:'fecha', label:'Fecha inicio', tipo:'date', col:2 },
    ]},
    { sec:'Registro de signos vitales', items:[
      { id:'filas_sv', label:'Registros', tipo:'tabla_dinamica',
        cols:['Fecha','Hora','T°C','T.A.','Pulso','Resp.','Sat O2','Orina ml','Observaciones','Firma'],
        iniciales: 0, autoFechaHora: true, autoFirma: true },
    ]},
  ],

  reporte_enfermera: [
    { sec:'Datos del turno', items:[
      { id:'turno',     label:'Turno', tipo:'select',
        ops:['Mañana (6:30am – 6:30pm)','Noche (6:30pm – 6:30am)'], col:2 },
      { id:'enfermera', label:'Enfermera responsable', tipo:'text', col:2 },
    ]},
    { sec:'Notas de enfermería', items:[
      { id:'filas_rpt', label:'Notas', tipo:'tabla_dinamica',
        cols:['Fecha y hora','Notas clínicas / Evolución','Firma'],
        iniciales: 0, autoFechaHora: true, autoFirma: true },
    ]},
  ],

  temp_pulso: [
    { sec:'Datos', items:[
      { id:'mes', label:'Mes', tipo:'select', ops:MESES, col:2 },
    ]},
  ],

  balance_hidrico: [
    { sec:'Datos del registro', items:[
      { id:'fecha_registro', label:'Fecha',     tipo:'date', col:3 },
      { id:'servicio',       label:'Servicio',  tipo:'text', col:3 },
      { id:'cama',           label:'No. Cama',  tipo:'text', col:3 },
      { id:'total_ingresos', label:'Total ingresos (ml)',    tipo:'text', col:3 },
      { id:'total_egresos',  label:'Total eliminaciones (ml)', tipo:'text', col:3 },
      { id:'balance',        label:'Balance 24h (ml)',        tipo:'text', col:3 },
    ]},
  ],

  tarjeta_medicamento: [
    { sec:'Datos clínicos', items:[
      { id:'diagnostico',    label:'Diagnóstico', tipo:'text' },
      { id:'alergias',       label:'Alérgico a',  tipo:'text', col:2 },
    ]},
    { sec:'Medicamentos y tratamiento', items:[
      { id:'filas_med', label:'Registro de medicamentos', tipo:'tabla_dinamica',
        cols:['Fecha','Cuidados','Exámenes','Tratamiento','7am-3pm','3pm-10pm','10pm-7am','Obs.'],
        iniciales:  0,
        autoFechaHora: true,
        addLabel:   '+ Agregar medicamento',
        emptyLabel: 'Sin registros — haz clic en "+ Agregar medicamento" para registrar un tratamiento.',
        autoFHLabel: '📅 La fecha se registra automáticamente del sistema al agregar cada fila. Las filas guardadas quedan bloqueadas para garantizar trazabilidad.',
      },
    ]},
  ],

  tarjeta_oral: [
    { sec:'Datos', items:[
      { id:'diagnostico',  label:'Diagnóstico',   tipo:'text' },
      { id:'fecha_inicio', label:'Fecha inicio',  tipo:'date', col:2 },
      { id:'alergias',     label:'Alérgico a',    tipo:'text', col:2 },
    ]},
    { sec:'Medicamentos vía oral', items:[
      { id:'filas_oral', label:'Medicamentos', tipo:'tabla_dinamica',
        cols:['Medicamento','Dosis','Freq.','Días','6am','12pm','6pm','10pm','Obs.'],
        iniciales: 8 },
    ]},
  ],

  tarjeta_parenteral: [
    { sec:'Datos', items:[
      { id:'diagnostico',  label:'Diagnóstico',  tipo:'text' },
      { id:'fecha_inicio', label:'Fecha inicio', tipo:'date', col:2 },
      { id:'alergias',     label:'Alérgico a',   tipo:'text', col:2 },
    ]},
    { sec:'Medicamentos parenterales', items:[
      { id:'filas_par', label:'Medicamentos', tipo:'tabla_dinamica',
        cols:['Medicamento / Solución','Vía','Dosis','7am-3pm','3pm-10pm','10pm-7am'],
        iniciales: 8 },
    ]},
  ],

  prog_cirugias: [
    { sec:'Datos generales', items:[
      { id:'fecha_cirugia', label:'Fecha de las cirugías', tipo:'date', col:2 },
    ]},
    { sec:'Quirófanos', items:[
      { id:'cirugias_multi', label:'Programación por quirófano', tipo:'tabla_cirugias_multi' },
    ]},
  ],

  gastos_sala: [
    { sec:'Datos de la cirugía', items:[
      { id:'fecha_cirugia',   label:'Fecha de cirugía',  tipo:'date', col:3 },
      { id:'quirofano',       label:'No. Quirófano',     tipo:'select', ops:['Quirófano 01','Quirófano 02'], col:3 },
      { id:'cirugia_realizada', label:'Cirugía realizada', tipo:'text' },
      { id:'ayudante',        label:'Ayudante',           tipo:'text', col:2 },
      { id:'anestesiologo',   label:'Anestesiólogo',      tipo:'text', col:2 },
      { id:'tipo_anestesia',  label:'Tipo de anestesia',  tipo:'select', ops:['General','Raquídea','Sedación','Local','Bloqueo'], col:2 },
      { id:'instrumentista',  label:'Enfermera instrumentista', tipo:'text', col:2 },
      { id:'hora_inicio',     label:'Hora inicio Cx',    tipo:'time', col:3 },
      { id:'hora_final',      label:'Hora final Cx',     tipo:'time', col:3 },
      { id:'tiempo_sala',     label:'Tiempo de sala',    tipo:'text', col:3 },
      { id:'particular',      label:'Particular',        tipo:'text', col:2 },
    ]},
    { sec:'Insumos y materiales utilizados', items:[
      { id:'insumos_gastos', label:'Insumos / materiales', tipo:'tabla_dinamica',
        cols:['Cantidad','Descripción del insumo / material','Observación'],
        iniciales: 15 },
    ]},
    { sec:'Observaciones', items:[
      { id:'observaciones', label:'Observaciones generales', tipo:'textarea' },
    ]},
  ],

  reporte_anestesia: [
    { sec:'Datos del anestesiólogo', items:[
      { id:'anestesiologo', label:'Nombre del anestesiólogo', tipo:'text', col:2 },
      { id:'fecha',         label:'Fecha',                    tipo:'date', col:2 },
      { id:'peso',          label:'Peso (kg)',                tipo:'text', col:3 },
      { id:'talla',         label:'Talla (cm)',               tipo:'text', col:3 },
      { id:'hora_inicio',   label:'Hora inicio anestesia',   tipo:'time', col:3 },
      { id:'hora_fin',      label:'Hora fin anestesia',      tipo:'time', col:3 },
      { id:'tipo_anestesia',label:'Tipo de anestesia',       tipo:'select', ops:['General','Raquídea','Sedación','Local'] },
    ]},
    { sec:'Medicamentos utilizados', items:[
      { id:'medicamentos_anest', label:'Medicamentos y cantidades', tipo:'tabla_dinamica',
        cols:['Medicamento','Cantidad','Medicamento','Cantidad'],
        iniciales: 18,
        filasFijas: [
          ['Sevorane','','Electrodos para monitoreo',''],['Isoflorane','','Set catéter epidural',''],
          ['Remifentanil','','Aguja punción lumbar',''],['Fentanil','','Tubo endotraqueal',''],
          ['Morfina','','Máscara laríngea',''],['Pethidine','','Sonda nasogástrica',''],
          ['Tramal','','Aguja hipodérmica',''],['Ketamina','','Solución salina normal',''],
          ['Propofol','','Hartmann',''],['Etomidato','','Suero mixto',''],
          ['Midazolam 15mg','','Oxígeno',''],['Cisatracurium','','Aire comprimido',''],
          ['Succinil colina','','Óxido nitroso',''],['Bupivacaína simple','','',''],
          ['Bupivacaína compuesta','','',''],['Lidocaína + epinefrina','','',''],
          ['Atropina 0.5/1mg','','',''],['Neostigmina','','',''],
        ],
      },
    ]},
    { sec:'Notas', items:[
      { id:'notas', label:'Notas / Complicaciones / Observaciones', tipo:'textarea', rows:4 },
    ]},
  ],

  reporte_operatorio: [
    { sec:'Equipo quirúrgico', items:[
      { id:'fecha_operacion', label:'Fecha de operación',      tipo:'date', col:2 },
      { id:'ayudante',        label:'1er. Ayudante',           tipo:'text', col:2 },
      { id:'ayudante2',       label:'2do. Ayudante',           tipo:'text', col:2 },
      { id:'anestesiologo',   label:'Anestesiólogo',           tipo:'text', col:2 },
      { id:'hora_inicio',     label:'Hora inicio',             tipo:'time', col:3 },
      { id:'hora_final',      label:'Hora final',              tipo:'time', col:3 },
      { id:'tiempo_op',       label:'Tiempo operatorio',       tipo:'text', col:3 },
      { id:'enf_circular',    label:'Enfermera circular',      tipo:'text', col:2 },
      { id:'enf_instrumentista', label:'Enf. instrumentista',  tipo:'text', col:2 },
    ]},
    { sec:'Diagnóstico y procedimiento', items:[
      { id:'operacion_practicada', label:'Operación practicada (codificación)', tipo:'text' },
      { id:'diag_pre',             label:'Diagnóstico preoperatorio',           tipo:'text' },
      { id:'diag_post',            label:'Diagnóstico postoperatorio',          tipo:'text' },
      { id:'drenajes',             label:'Drenajes',                            tipo:'text', col:2 },
      { id:'biopsia',              label:'Biopsia',                             tipo:'text', col:2 },
      { id:'lab_intra',            label:'Laboratorio intraoperatorio',         tipo:'text', col:2 },
      { id:'descripcion_tecnica',  label:'Descripción de técnica quirúrgica',   tipo:'textarea', rows:8 },
    ]},
  ],

  honorarios: [
    { sec:'Datos del pago', items:[
      { id:'correlativo',  label:'Correlativo No.',     tipo:'text', col:2 },
      { id:'fecha',        label:'Fecha',               tipo:'date', col:2 },
      { id:'doctor',       label:'Doctor/a',            tipo:'text' },
      { id:'monto',        label:'Monto ($)',           tipo:'text', col:2 },
      { id:'monto_texto',  label:'Monto en letras',     tipo:'text', col:2 },
      { id:'procedimiento',label:'Procedimiento / Concepto', tipo:'text' },
      { id:'tipo_doc',     label:'Tipo de documento',   tipo:'select', ops:['Recibo','Factura','CCF'], col:3 },
      { id:'num_doc',      label:'No. del documento',   tipo:'text', col:3 },
      { id:'fecha_doc',    label:'Fecha del documento', tipo:'date', col:3 },
    ]},
  ],

  asignaciones: [
    { sec:'Datos', items:[
      { id:'fecha', label:'Fecha', tipo:'date', col:2 },
    ]},
  ],

  reporte_insumos: [
    { sec:'Datos del reporte', items:[
      { id:'fecha',       label:'Fecha',           tipo:'date', col:3 },
      { id:'hora',        label:'Hora',            tipo:'time', col:3 },
      { id:'correlativo', label:'Correlativo No.', tipo:'text', col:3 },
    ]},
    { sec:'Insumos utilizados', items:[
      { id:'insumos_rows', label:'Insumos', tipo:'tabla_dinamica',
        cols:['Cantidad','Presentación','Descripción del producto'],
        iniciales: 10 },
    ]},
  ],

  req_productos: [
    { sec:'Datos de la requisición', items:[
      { id:'fecha',       label:'Fecha',           tipo:'date', col:3 },
      { id:'hora',        label:'Hora',            tipo:'time', col:3 },
      { id:'correlativo', label:'Correlativo No.', tipo:'text', col:3 },
    ]},
    { sec:'Productos solicitados', items:[
      { id:'productos_rows', label:'Productos', tipo:'tabla_dinamica',
        cols:['Cantidad','Presentación','Descripción del producto'],
        iniciales: 10 },
    ]},
  ],

  control_temp_refri: [
    { sec:'Registros de temperatura', items:[
      { id:'filas_temp', label:'Registros', tipo:'tabla_dinamica',
        cols:['Fecha','Hora','Temperatura encontrada (°C)','Firma y sello de quien verificó'],
        iniciales: 20 },
    ]},
  ],

  lista_asistencia: [
    { sec:'Datos del evento', items:[
      { id:'nombre_capacitacion', label:'Nombre de la capacitación', tipo:'text' },
      { id:'fecha_evento',        label:'Fecha del evento',          tipo:'date', col:2 },
      { id:'impartido_por',       label:'Impartido por',            tipo:'text', col:2 },
      { id:'lugar',               label:'Lugar',                    tipo:'text', col:2 },
    ]},
  ],

  acciones_personal: [
    { sec:'Datos del empleado', items:[
      { id:'nombre_empleado', label:'Nombre del empleado', tipo:'text', col:2 },
      { id:'area_labora',     label:'Área donde labora',  tipo:'text', col:2 },
      { id:'cargo',           label:'Cargo',              tipo:'text', col:2 },
    ]},
  ],
};

// ════════════════════════════════════════════════════════════
//  EDITOR DE FORMULARIO
// ════════════════════════════════════════════════════════════
function CampoSiNo({ campo, valor, setValor, disabled = false }) {
  const v = valor[campo.id] || '';
  const vDet = valor[campo.id + '_detalle'] || '';
  return (
    <div style={S.campo}>
      <label style={S.label}>{campo.label}</label>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
        <div style={{ ...S.sinoWrap, opacity: disabled ? 0.75 : 1 }}>
          <button disabled={disabled} style={{ ...S.sinoBtn(v==='si','#1D9E75'), cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1 }} onClick={()=>!disabled && setValor(prev=>({...prev,[campo.id]:'si'}))}>Sí</button>
          <button disabled={disabled} style={{ ...S.sinoBtn(v==='no','#dc3545'), cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1 }} onClick={()=>!disabled && setValor(prev=>({...prev,[campo.id]:'no'}))}>No</button>
        </div>
        {(campo.detalle || campo.obs) && (
          <input
            style={{ ...S.input, flex:1, minWidth:'180px', background: disabled ? '#f8fafd' : '#fff', color: disabled ? '#495057' : '#333' }}
            placeholder={campo.obs ? 'Observación...' : 'Especifique...'}
            value={vDet}
            onChange={e=>!disabled && setValor(prev=>({...prev,[campo.id+'_detalle']:e.target.value}))}
            readOnly={disabled}
          />
        )}
      </div>
      {disabled && <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'4px' }}>Registro bloqueado luego de guardarse.</div>}
    </div>
  );
}

function TablaDinamica({ campo, valor, setValor, usuario, lockCount = 0, onGuardarFila, guardandoFila = false }) {
  const key    = campo.id;
  const autoFH = !!campo.autoFechaHora;
  const autoFm = !!campo.autoFirma;
  const [firmaImg, setFirmaImg] = useState(null);
  const [filaGuardandoIdx, setFilaGuardandoIdx] = useState(null); // índice de fila en proceso de guardado individual
  const [filaOkIdx,        setFilaOkIdx]        = useState(null); // índice de fila con confirmación "✓ Guardado" reciente

  // v3: cargar firma+sello combinados (firma_sello_ruta) con fallback a firma_ruta legacy
  useEffect(() => {
    if (!autoFm || !usuario) return;
    const ruta = usuario.firma_sello_ruta || usuario.firma_ruta || null;
    if (!ruta) return;
    // Antes: window.altimo.usuarios.leerFirma({ ruta }) leía el archivo del
    // disco y lo convertía a base64. Ahora la ruta ya es una URL pública.
    setFirmaImg(api.lab.urlArchivo(ruta));
  }, [usuario?.id, autoFm]);

  const sourceField = campo.prefillFromField || null;
  const sourceRows = sourceField ? (valor[sourceField] || []) : [];
  const targetColIndex = campo.prefillTargetCol ? campo.cols.indexOf(campo.prefillTargetCol || '') : -1;
  const sourceOptions = sourceField
    ? sourceRows
        .map((row, idx) => ({
          idx,
          texto: String(row?.[2] || '').trim(),
          medico: String(row?.[1] || '').trim(),
          fecha: String(row?.[0] || '').trim(),
          resumen: [String(row?.[1] || '').trim(), String(row?.[0] || '').trim()].filter(Boolean).join(' · '),
        }))
        .filter(opt => opt.texto)
    : [];
  const [selectedPrefill, setSelectedPrefill] = useState('');

  useEffect(() => {
    if (!sourceOptions.length) {
      setSelectedPrefill('');
      return;
    }
    setSelectedPrefill(prev =>
      sourceOptions.some(opt => opt.texto === prev) ? prev : sourceOptions[sourceOptions.length - 1].texto
    );
  }, [sourceOptions.length, JSON.stringify(sourceOptions.map(o => o.texto))]);

  const init = () => {
    if (campo.filasFijas) return campo.filasFijas.map(f => [...f]);
    const n = (campo.iniciales !== undefined) ? campo.iniciales : 5;
    return Array(n).fill(null).map(() => campo.cols.map(() => ''));
  };

  const filas    = valor[key] || init();
  const setFilas = (nf) => setValor(prev => ({ ...prev, [key]: nf }));
  const setCell  = (r, c, v) => { const nf = [...filas]; nf[r] = [...nf[r]]; nf[r][c] = v; setFilas(nf); };
  const delFila  = (i) => {
    if (i < lockCount) return;
    const nf = [...filas];
    nf.splice(i, 1);
    setFilas(nf);
  };

  const ahora = () => {
    const d = new Date();
    const fecha = d.toLocaleDateString('es-SV', { day:'2-digit', month:'2-digit', year:'numeric' });
    const hora  = d.toLocaleTimeString('es-SV', { hour:'2-digit', minute:'2-digit', hour12: true });
    return `${fecha} ${hora}`;
  };

  const esFechaHoraCol = (col) =>
    col === 'Fecha y hora' || col === 'Fecha' || col === 'Hora' ||
    col === 'Fecha/Hora cumplimiento';
  const esFirmaCol = (col, ci) => autoFm && ci === campo.cols.length - 1;
  const esNotaCol  = (col) => [
    'Notas clínicas / Evolución','Notas clínicas','Notas clínicas y evolución',
    'Evolución / Indicaciones médicas','Notas',
    'Indicaciones / Prescripciones','Descripción de la prescripción',
  ].includes(col);
  const esNotaTabla = campo.cols.some(c => esNotaCol(c));
  const addLabel = campo.addLabel || (esNotaTabla ? '+ Nueva nota' : '+ Fila');
  const requiresSourceSelection = !!sourceField;
  const sourceSelectionValida = !requiresSourceSelection || !!selectedPrefill;
  const isPrefilledLockedCol = (ci) => requiresSourceSelection && ci === targetColIndex;

  const addFila = () => {
    if (requiresSourceSelection && !selectedPrefill) return;
    const ts   = ahora();
    const hoy  = new Date().toLocaleDateString('es-SV');
    const hora = new Date().toLocaleTimeString('es-SV', { hour:'2-digit', minute:'2-digit', hour12: true });
    const fila = campo.cols.map((col, ci) => {
      if (autoFH && esFechaHoraCol(col)) return ts;
      if (autoFH && col === 'Fecha') return hoy;
      if (autoFH && col === 'Hora')  return hora;
      if (autoFm && ci === campo.cols.length - 1) return usuario?.nombre || '';
      if (sourceField && targetColIndex === ci) return selectedPrefill || '';
      return '';
    });
    // Functional update para evitar stale closure — nunca pierde notas previas
    setValor(prev => {
      const current = prev[key] || init();
      return { ...prev, [key]: [...current, fila] };
    });
  };

  // Guarda esta fila sin obligar al usuario a desplazarse hasta el botón
  // "Guardar" general del formulario. Reutiliza el mismo guardado global
  // (no existe — ni debe existir — un guardado parcial a nivel de BD;
  // el documento es un único JSON por admisión+formulario), pero desde la
  // perspectiva del usuario el resultado es "guardé esta indicación ya".
  const guardarFilaIndividual = async (ri) => {
    if (!onGuardarFila) return;
    setFilaGuardandoIdx(ri);
    setFilaOkIdx(null);
    try {
      await onGuardarFila();
      setFilaOkIdx(ri);
      setTimeout(() => setFilaOkIdx(prev => (prev === ri ? null : prev)), 2500);
    } finally {
      setFilaGuardandoIdx(null);
    }
  };

  const filaTieneContenido = (fila) => fila.some(c => String(c || '').trim() !== '');

  return (
    <div style={S.campo}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px', gap:'10px', flexWrap:'wrap' }}>
        <label style={S.label}>{campo.label}</label>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
          {sourceField && (
            <>
              <span style={{ fontSize:'11px', fontWeight:'600', color:'#6c757d', textTransform:'uppercase', letterSpacing:'.04em' }}>
                {campo.prefillLabel || 'Seleccionar referencia'}
              </span>
              <select
                value={selectedPrefill}
                onChange={e => setSelectedPrefill(e.target.value)}
                style={{ ...S.input, width:'360px', padding:'6px 10px', fontSize:'12px' }}
              >
                <option value="">— Seleccionar —</option>
                {sourceOptions.map((opt, idx) => (
                  <option key={`${opt.fecha}-${idx}`} value={opt.texto}>
                    {((opt.resumen ? `${opt.resumen} · ` : '') + opt.texto).slice(0, 220)}
                  </option>
                ))}
              </select>
            </>
          )}
          <button
            style={{ background: esNotaTabla ? '#1D9E75' : '#1B2A5E', color:'#fff', border:'none',
              borderRadius:'7px', padding:'5px 14px', fontSize:'11px', fontWeight:'700', cursor: sourceSelectionValida ? 'pointer' : 'not-allowed', opacity: sourceSelectionValida ? 1 : .6 }}
            onClick={addFila}
            disabled={!sourceSelectionValida}
            title={!sourceSelectionValida ? 'Primero selecciona una orden médica' : ''}>
            {addLabel}
          </button>
        </div>
      </div>

      {autoFH && (
        <div style={{ background:'#e8f0fb', border:'1px solid #c8ddf7', borderRadius:'7px', padding:'6px 12px',
          fontSize:'11px', color:'#1B2A5E', marginBottom:'8px' }}>
          {campo.autoFHLabel
            ? campo.autoFHLabel
            : <>
                🕐 La fecha y hora se registran automáticamente al crear cada nueva nota.
                {autoFm && usuario?.nombre && ` · Firmante: ${usuario.nombre}`}
                {sourceField && ` · ${campo.prefillHelp || 'La descripción se puede precargar desde una orden médica ya registrada.'}`}
              </>
          }
        </div>
      )}

      {filas.length === 0 ? (
        <div style={{ textAlign:'center', padding:'24px', color:'#adb5bd', fontSize:'12px',
          border:'2px dashed #dde3ea', borderRadius:'8px' }}>
          {campo.emptyLabel || `Sin registros — haz clic en "${addLabel}" para agregar.`}
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={S.tabDin}>
            <thead>
              <tr>
                {campo.cols.map((c, i) => (
                  <th key={i} style={{
                    ...S.tabTh,
                    minWidth: esNotaCol(c) ? '320px' : esFechaHoraCol(c) ? '130px' :
                              esFirmaCol(c, i) ? '160px' : '80px',
                  }}>{c}</th>
                ))}
                <th style={{ ...S.tabTh, width: onGuardarFila ? '130px' : '30px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, ri) => {
                const filaGuardada = ri < lockCount;
                return (
                <tr key={ri} style={{
                  background: filaGuardada ? '#f0f4f8' : (ri % 2 === 0 ? '#fff' : '#f8fafd'),
                  verticalAlign:'top',
                  opacity: filaGuardada ? .92 : 1,
                  borderLeft: filaGuardada ? '3px solid #1B2A5E' : '3px solid transparent',
                }}>
                  {fila.map((cel, ci) => {
                    const col = campo.cols[ci];
                    const bloqueada = ri < lockCount;
                    // ── Fecha/hora: solo lectura si autoFH o si la fila ya fue guardada ──
                    if (esFechaHoraCol(col) && (autoFH || bloqueada)) {
                      return (
                        <td key={ci} style={{ ...S.tabTd, verticalAlign:'middle' }}>
                          <div style={{
                            background: bloqueada ? '#e8f0fb' : '#e8f0fb',
                            borderRadius:'5px', padding:'5px 8px',
                            fontSize:'11px', fontWeight:'700', color:'#1B2A5E', whiteSpace:'nowrap',
                            border: bloqueada ? '1px solid #c8ddf7' : 'none',
                          }}>
                            🔒 {cel || '—'}
                          </div>
                        </td>
                      );
                    }
                    // ── Columna de notas: textarea que crece ──
                    if (esNotaCol(col)) {
                      const prefijada = isPrefilledLockedCol(ci);
                      return (
                        <td key={ci} style={S.tabTd}>
                          <textarea
                            style={{ ...S.tabIn, minHeight:'80px', resize:'vertical', fontFamily:'inherit', lineHeight:'1.5', background: (bloqueada || prefijada) ? '#f8fafd' : '#fff', color: (bloqueada || prefijada) ? '#495057' : '#333' }}
                            value={cel}
                            onChange={e => setCell(ri, ci, e.target.value)}
                            placeholder={prefijada ? 'Descripción cargada desde la orden médica' : 'Escriba aquí las observaciones...'}
                            readOnly={bloqueada || prefijada}
                            title={prefijada ? 'Este texto proviene de la orden médica y no debe alterarse aquí.' : ''}
                          />
                        </td>
                      );
                    }
                    // ── Columna firma: muestra nombre + imagen firma+sello combinada ──
                    if (esFirmaCol(col, ci) && autoFm) {
                      return (
                        <td key={ci} style={{ ...S.tabTd, verticalAlign:'middle', minWidth:'180px' }}>
                          <div style={{ background:'#f8fafd', border:'1px solid #dde3ea', borderRadius:'8px', padding:'10px 12px', textAlign:'center' }}>
                            <div style={{ fontSize:'12px', fontWeight:'700', color:'#1B2A5E', marginBottom:'6px' }}>
                              {cel || usuario?.nombre || ''}
                              {usuario?.titulo ? <span style={{ fontSize:'10px', fontWeight:'400', color:'#6c757d', display:'block' }}>{usuario.titulo}</span> : null}
                            </div>
                            {firmaImg ? (
                              <img src={firmaImg} alt="Firma y sello"
                                style={{ maxHeight:'72px', maxWidth:'220px', objectFit:'contain', display:'block', margin:'0 auto' }} />
                            ) : (
                              <div style={{ fontSize:'10px', color:'#adb5bd', margin:'4px 0' }}>— sin firma/sello cargado —</div>
                            )}
                          </div>
                        </td>
                      );
                    }
                    // ── Campo normal ──
                    const prefijada = isPrefilledLockedCol(ci);
                    return (
                      <td key={ci} style={S.tabTd}>
                        <input
                          style={{ ...S.tabIn, background: (bloqueada || prefijada) ? '#f8fafd' : '#fff', color: (bloqueada || prefijada) ? '#495057' : '#333' }}
                          value={cel}
                          onChange={e => setCell(ri, ci, e.target.value)}
                          readOnly={bloqueada || prefijada}
                          title={prefijada ? 'Este texto proviene de la orden médica y no debe alterarse aquí.' : ''}
                        />
                      </td>
                    );
                  })}
                  <td style={{ ...S.tabTd, textAlign:'center', verticalAlign:'middle', whiteSpace:'nowrap' }}>
                    {onGuardarFila && !filaGuardada && filaTieneContenido(fila) && (
                      filaOkIdx === ri ? (
                        <span style={{ color:'#1D9E75', fontSize:'11px', fontWeight:700, marginRight:'8px' }}>✓ Guardado</span>
                      ) : (
                        <button
                          onClick={() => guardarFilaIndividual(ri)}
                          disabled={guardandoFila || filaGuardandoIdx === ri}
                          title="Guardar esta indicación ahora, sin esperar al botón de guardar general"
                          style={{
                            background:'#1D9E75', color:'#fff', border:'none', borderRadius:'6px',
                            padding:'4px 10px', fontSize:'11px', fontWeight:700, marginRight:'8px',
                            cursor: (guardandoFila || filaGuardandoIdx === ri) ? 'not-allowed' : 'pointer',
                            opacity: (guardandoFila || filaGuardandoIdx === ri) ? .6 : 1,
                          }}>
                          {filaGuardandoIdx === ri ? 'Guardando…' : '💾 Guardar'}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => delFila(ri)}
                      disabled={ri < lockCount}
                      title={ri < lockCount ? 'Registro ya guardado: no editable' : 'Eliminar fila'}
                      style={{ background:'none', border:'none', cursor: ri < lockCount ? 'not-allowed' : 'pointer', color: ri < lockCount ? '#adb5bd' : '#dc3545', fontSize:'14px' }}>✕</button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  TABLA ESPECIAL: Programación de cirugías — MULTI-QUIRÓFANO
//  ✓ Configurable: agregar quirófanos en QUIROFANOS_CONFIG
//  ✓ Tabs selector por quirófano
//  ✓ Todo en MAYÚSCULAS automático
//  ✓ Fecha/Hora Anotación auto al guardar (col 6)
//  ✓ Estado por fila: PROGRAMADA / REPROGRAMADA / REALIZADA
//  ✓ Reprogramada conserva timestamp original + historial
//  ✓ Búsqueda de paciente ingresado con auto-relleno
// ════════════════════════════════════════════════════════════

const QUIROFANOS_CONFIG = [
  { id: 'q1', label: 'Quirófano 1', color: '#993C1D' },
  { id: 'q2', label: 'Quirófano 2', color: '#185FA5' },
  // { id: 'q3', label: 'Quirófano 3', color: '#1D9E75' },
  // { id: 'q4', label: 'Quirófano 4', color: '#5F5E5A' },
];

const HORAS_CX    = ['5am','6am','7am','8am','9am','10am','11am','12pm','1pm','2pm','3pm','4pm','5pm','6pm','7pm'];
const COLS_CX     = ['Hora','Cirujano','Paciente','Nombre de la Cirugía','Anestesiólogo','Otros','Fecha y Hora Anotación','Responsable','Observaciones'];
const CX_TS_IDX   = 6;   // Fecha y Hora Anotación — auto, NUNCA cambia
const CX_OBS_IDX  = 8;   // Observaciones — siempre editable
const CX_EST_IDX  = 9;   // Estado (PROGRAMADA / REPROGRAMADA / REALIZADA)
const CX_HIST_IDX = 10;  // Historial JSON (reprogramaciones)

const ESTADOS_CX = [
  { id:'PROGRAMADA',   label:'Programada',   color:'#1565c0', bg:'#dbeafe', border:'#93c5fd', icon:'📋' },
  { id:'REPROGRAMADA', label:'Reprogramada', color:'#c45000', bg:'#fff3e0', border:'#fdba74', icon:'🔄' },
  { id:'REALIZADA',    label:'Realizada',    color:'#166534', bg:'#dcfce7', border:'#86efac', icon:'✅' },
];

// Normaliza filas antiguas (9 cols) → nuevas (11 cols)
const normFila = (row) => {
  const r = Array.isArray(row) ? [...row] : [row,...Array(10).fill('')];
  while (r.length < 9)  r.push('');
  if (r.length === 9)  { r.push(''); r.push('[]'); }
  if (r.length === 10) { r.push('[]'); }
  return r;
};
const initFilasCX = () => HORAS_CX.map(h => [h,'','','','','','','','','','[]']);

const tsAhora = () => {
  const d = new Date();
  return d.toLocaleDateString('es-SV',{day:'2-digit',month:'2-digit',year:'numeric'}) +
         ' ' + d.toLocaleTimeString('es-SV',{hour:'2-digit',minute:'2-digit',hour12:true});
};

// ─── Tabla de un quirófano ───────────────────────────────────
function TablaCirugias({ dataKey, valor, setValor }) {
  const rawFilas = valor[dataKey];
  const filas = (Array.isArray(rawFilas) && rawFilas.length === 15)
    ? rawFilas.map(normFila)
    : initFilasCX();

  // Modales
  const [modalPac,  setModalPac]  = useState(null);  // { ri } — búsqueda paciente
  const [modalRep,  setModalRep]  = useState(null);  // { ri, nueva_hora } — reprogramar
  const [busqPac,   setBusqPac]   = useState('');
  const [resPac,    setResPac]    = useState([]);
  const [cargPac,   setCargPac]   = useState(false);
  const [histVer,   setHistVer]   = useState(null);  // ri cuyo historial se muestra

  const setFilas = (nf) => setValor(prev => ({ ...prev, [dataKey]: nf }));

  const setCell = (ri, ci, v) => {
    const val = v.toUpperCase();
    setFilas(filas.map((r, idx) =>
      idx === ri ? r.map((c, cidx) => cidx === ci ? val : c) : r
    ));
  };

  const tieneDatos = (row) =>
    [1,2,3,4,5,7].some(ci => String(row[ci] || '').trim() !== '');

  const isGuardada = (row) =>
    tieneDatos(row) && String(row[CX_TS_IDX] || '').trim() !== '';

  const getEstado = (row) => String(row[CX_EST_IDX] || '');
  const getHist   = (row) => { try { return JSON.parse(row[CX_HIST_IDX] || '[]'); } catch(_){ return []; } };

  const rowBg = (row, ri) => {
    const est = getEstado(row);
    const e = ESTADOS_CX.find(e => e.id === est);
    if (e) return e.bg;
    return ri % 2 === 0 ? '#fff' : '#f8fafd';
  };
  const rowBorder = (row) => {
    const est = getEstado(row);
    const e = ESTADOS_CX.find(e => e.id === est);
    return e ? `3px solid ${e.border}` : 'none';
  };

  // Cambiar estado
  const cambiarEstado = (ri, nuevoEstado) => {
    if (nuevoEstado === 'REPROGRAMADA') {
      setModalRep({ ri, nueva_hora: '' });
      return;
    }
    setFilas(filas.map((r, idx) =>
      idx === ri ? r.map((c, ci) => ci === CX_EST_IDX ? nuevoEstado : c) : r
    ));
  };

  // Confirmar reprogramación
  const confirmarReprog = () => {
    if (!modalRep) return;
    const { ri, nueva_hora } = modalRep;
    const fila = filas[ri];
    const tsOrig = fila[CX_TS_IDX] || '';
    const ahora  = tsAhora();
    let hist = getHist(fila);
    hist.push({ tipo:'REPROGRAMADA', ts_anterior: tsOrig, nueva_hora: nueva_hora.toUpperCase(), anotado_en: ahora });
    const notaObs = `[REPROG. ${ahora}${nueva_hora ? ' → '+nueva_hora.toUpperCase() : ''}]`;
    setFilas(filas.map((r, idx) => {
      if (idx !== ri) return r;
      return r.map((c, ci) => {
        if (ci === CX_EST_IDX)  return 'REPROGRAMADA';
        if (ci === CX_HIST_IDX) return JSON.stringify(hist);
        if (ci === CX_OBS_IDX)  return (c ? c+'\n'+notaObs : notaObs);
        return c;
      });
    }));
    setModalRep(null);
  };

  // Búsqueda de paciente ingresado
  const buscarPacientes = async (q) => {
    setBusqPac(q);
    if (q.length < 2) { setResPac([]); return; }
    setCargPac(true);
    try {
      const r = await api.admisiones.buscar(q);
      const lista = r?.data || r || [];
      setResPac(lista.filter(p =>
        String(p.estado || '').toLowerCase() === 'activa'
      ));
    } catch(_) { setResPac([]); }
    setCargPac(false);
  };

  const seleccionarPaciente = (ri, pac) => {
    setFilas(filas.map((r, idx) => {
      if (idx !== ri) return r;
      return r.map((c, ci) => {
        if (ci === 2) return String(pac.paciente || '').toUpperCase();
        if (ci === 1) return String(pac.medico   || c || '').toUpperCase();
        if (ci === 7) return String(pac.habitacion ? 'HAB. '+pac.habitacion : c).toUpperCase();
        return c;
      });
    }));
    setModalPac(null);
    setBusqPac('');
    setResPac([]);
  };

  const minW = (ci) => {
    if (ci === 0) return '48px';
    if (ci === CX_TS_IDX) return '102px';
    if (ci === CX_OBS_IDX) return '170px';
    if (ci === 3) return '120px';
    return '75px';
  };

  return (
    <div style={S.campo}>
      {/* Info banner */}
      <div style={{ background:'#e8f0fb', border:'1px solid #c8ddf7', borderRadius:'7px', padding:'7px 12px', fontSize:'11px', color:'#1B2A5E', marginBottom:'8px', display:'flex', gap:'12px', flexWrap:'wrap', alignItems:'center' }}>
        <span>🕐 <strong>Fecha y Hora Anotación</strong> = automática al guardar (no modificable).</span>
        <span>🔍 Usa el botón de lupa para auto-rellenar datos de paciente ya ingresado.</span>
        <span>Todo en <strong>MAYÚSCULAS</strong>.</span>
      </div>

      {/* Leyenda de estados */}
      <div style={{ display:'flex', gap:'6px', marginBottom:'8px', flexWrap:'wrap' }}>
        {ESTADOS_CX.map(e => (
          <div key={e.id} style={{ display:'flex', alignItems:'center', gap:'4px', padding:'3px 10px', background:e.bg, border:`1px solid ${e.border}`, borderRadius:'20px', fontSize:'10px', fontWeight:'700', color:e.color }}>
            {e.icon} {e.label}
          </div>
        ))}
        <div style={{ display:'flex', alignItems:'center', gap:'4px', padding:'3px 10px', background:'#f8fafd', border:'1px solid #dde3ea', borderRadius:'20px', fontSize:'10px', color:'#6c757d' }}>
          Sin estado
        </div>
      </div>

      <div style={{ overflowX:'auto', position:'relative' }}>
        <table style={{ ...S.tabDin, minWidth:'960px' }}>
          <thead>
            <tr>
              {COLS_CX.map((c, ci) => (
                <th key={ci} style={{ ...S.tabTh, minWidth:minW(ci), fontSize:'10px', padding:'5px 5px', whiteSpace:'nowrap',
                  background: ci === CX_TS_IDX ? '#1D9E75' : ci === CX_OBS_IDX ? '#185FA5' : '#1B2A5E' }}>
                  {c}
                  {ci === CX_TS_IDX  && <div style={{ fontSize:'8px', fontWeight:'400', opacity:.8 }}>auto — sistema</div>}
                  {ci === CX_OBS_IDX && <div style={{ fontSize:'8px', fontWeight:'400', opacity:.8 }}>siempre editable</div>}
                </th>
              ))}
              <th style={{ ...S.tabTh, minWidth:'130px', fontSize:'10px', padding:'5px', background:'#534AB7' }}>
                Estado
                <div style={{ fontSize:'8px', fontWeight:'400', opacity:.8 }}>click para cambiar</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filas.map((row, ri) => {
              const guardada = isGuardada(row);
              const conDatos = tieneDatos(row);
              const estado   = getEstado(row);
              const hist     = getHist(row);
              const estInfo  = ESTADOS_CX.find(e => e.id === estado);

              return (
                <React.Fragment key={ri}>
                  <tr style={{ background: rowBg(row, ri), verticalAlign:'middle', borderLeft: rowBorder(row) }}>
                    {row.slice(0, 9).map((cel, ci) => {
                      // Col 0 — Hora fija
                      if (ci === 0) return (
                        <td key={ci} style={{ ...S.tabTd, background:'#e8f0fb', fontWeight:'700', fontSize:'12px', whiteSpace:'nowrap', padding:'5px 7px', textAlign:'center' }}>
                          {cel}
                        </td>
                      );
                      // Col 6 — Fecha/Hora Anotación (auto)
                      if (ci === CX_TS_IDX) return (
                        <td key={ci} style={{ ...S.tabTd, background:guardada?'#f0fff8':'transparent', padding:'4px 5px' }}>
                          {cel
                            ? <div>
                                <div style={{ fontSize:'10px', fontWeight:'700', color:'#1D9E75', whiteSpace:'nowrap' }}>🕐 {cel}</div>
                                {hist.length > 0 && (
                                  <button onClick={() => setHistVer(histVer === ri ? null : ri)}
                                    style={{ fontSize:'9px', color:'#c45000', background:'none', border:'none', cursor:'pointer', padding:'2px 0', fontWeight:'700' }}>
                                    {histVer === ri ? '▲ ocultar' : `🔄 ${hist.length} reprog.`}
                                  </button>
                                )}
                              </div>
                            : <div style={{ fontSize:'10px', color:'#adb5bd', fontStyle:'italic' }}>Al guardar</div>}
                        </td>
                      );
                      // Col 8 — Observaciones (siempre editable)
                      if (ci === CX_OBS_IDX) return (
                        <td key={ci} style={{ ...S.tabTd, minWidth:'170px' }}>
                          <textarea
                            style={{ ...S.tabIn, minHeight:'44px', resize:'vertical', fontFamily:'inherit', fontSize:'11px',
                              background:'#fffbeb', border:'1px solid #fbbf24', textTransform:'uppercase' }}
                            value={cel}
                            onChange={e => setCell(ri, ci, e.target.value)}
                            placeholder="OBSERVACIONES..."
                          />
                        </td>
                      );
                      // Col 2 — Paciente (con botón de búsqueda de ingresados)
                      if (ci === 2) return (
                        <td key={ci} style={S.tabTd}>
                          <div style={{ display:'flex', gap:'3px', alignItems:'center' }}>
                            <input
                              style={{ ...S.tabIn, fontSize:'11px', textTransform:'uppercase', flex:1,
                                background: guardada ? '#f0f4f8' : '#fff',
                                color:      guardada ? '#6c757d' : '#222',
                                cursor:     guardada ? 'not-allowed' : 'text' }}
                              value={cel}
                              onChange={e => !guardada && setCell(ri, ci, e.target.value)}
                              readOnly={guardada}
                            />
                            {!guardada && (
                              <button
                                onClick={() => { setModalPac({ ri }); setBusqPac(''); setResPac([]); }}
                                title="Buscar paciente ya ingresado"
                                style={{ background:'#1B2A5E', color:'#fff', border:'none', borderRadius:'5px',
                                  padding:'4px 6px', cursor:'pointer', fontSize:'12px', flexShrink:0 }}>
                                🔍
                              </button>
                            )}
                          </div>
                        </td>
                      );
                      // Cols 1,3,4,5,7 — datos (bloqueados si guardada)
                      return (
                        <td key={ci} style={S.tabTd}>
                          <input
                            style={{ ...S.tabIn, fontSize:'11px', textTransform:'uppercase',
                              background: guardada ? '#f0f4f8' : '#fff',
                              color:      guardada ? '#6c757d' : '#222',
                              cursor:     guardada ? 'not-allowed' : 'text' }}
                            value={cel}
                            onChange={e => !guardada && setCell(ri, ci, e.target.value)}
                            readOnly={guardada}
                            title={guardada ? '🔒 Guardada — solo editar Observaciones' : ''}
                            placeholder={guardada ? '' : COLS_CX[ci].toUpperCase().slice(0,12)}
                          />
                        </td>
                      );
                    })}
                    {/* Columna Estado */}
                    <td style={{ ...S.tabTd, minWidth:'130px', verticalAlign:'middle', padding:'5px' }}>
                      {conDatos ? (
                        <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
                          {ESTADOS_CX.map(e => (
                            <button key={e.id}
                              onClick={() => cambiarEstado(ri, e.id)}
                              style={{
                                padding:'4px 8px', border:`1px solid ${e.border}`, borderRadius:'5px',
                                cursor:'pointer', fontSize:'10px', fontWeight:'700', textAlign:'left',
                                background: estado === e.id ? e.bg : '#f8fafd',
                                color:      estado === e.id ? e.color : '#6c757d',
                                boxShadow:  estado === e.id ? `0 1px 4px ${e.border}` : 'none',
                              }}>
                              {e.icon} {e.label}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize:'10px', color:'#c0ccda', textAlign:'center' }}>—</div>
                      )}
                    </td>
                  </tr>

                  {/* Sub-fila: historial de reprogramaciones */}
                  {histVer === ri && hist.length > 0 && (
                    <tr>
                      <td colSpan={COLS_CX.length + 1} style={{ padding:'0 0 6px 60px', background:'#fff8f0' }}>
                        <div style={{ padding:'8px 12px', fontSize:'11px' }}>
                          <div style={{ fontWeight:'700', color:'#c45000', marginBottom:'6px' }}>🔄 Historial de reprogramaciones</div>
                          {hist.map((h, hi) => (
                            <div key={hi} style={{ display:'flex', gap:'12px', padding:'4px 8px', background:'#fff', borderLeft:'3px solid #fdba74', marginBottom:'4px', borderRadius:'0 4px 4px 0', fontSize:'10px' }}>
                              <div><span style={{ color:'#6c757d' }}>Anotada el:</span> <strong>{h.ts_anterior || '—'}</strong></div>
                              <div><span style={{ color:'#6c757d' }}>Nueva hora:</span> <strong style={{ color:'#c45000' }}>{h.nueva_hora || '—'}</strong></div>
                              <div><span style={{ color:'#6c757d' }}>Registrado:</span> <strong>{h.anotado_en}</strong></div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ─── MODAL: Buscar paciente ingresado ─── */}
      {modalPac && (
        <div style={{ position:'fixed', inset:0, background:'rgba(10,20,55,.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setModalPac(null)}>
          <div style={{ background:'#fff', borderRadius:'14px', padding:'24px', width:'520px', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,.35)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'15px', fontWeight:'800', color:'#1B2A5E', marginBottom:'14px' }}>
              🔍 Buscar paciente ingresado
            </div>
            <div style={{ fontSize:'11px', color:'#6c757d', marginBottom:'12px', background:'#e8f0fb', padding:'8px 10px', borderRadius:'7px' }}>
              Solo se muestran pacientes con <strong>admisión activa</strong>. Al seleccionar, se auto-rellenan: Paciente, Cirujano y Responsable/Habitación.
            </div>
            <input
              autoFocus
              style={{ ...S.input, marginBottom:'10px' }}
              placeholder="Nombre, apellido o DUI..."
              value={busqPac}
              onChange={e => buscarPacientes(e.target.value)}
            />
            {cargPac && <div style={{ textAlign:'center', color:'#6c757d', fontSize:'12px', padding:'10px' }}>Buscando...</div>}
            {!cargPac && resPac.length === 0 && busqPac.length >= 2 && (
              <div style={{ textAlign:'center', color:'#adb5bd', fontSize:'12px', padding:'16px' }}>
                Sin pacientes activos con ese criterio.
              </div>
            )}
            {resPac.map(p => (
              <div key={p.id}
                onClick={() => seleccionarPaciente(modalPac.ri, p)}
                style={{ padding:'10px 12px', borderRadius:'8px', marginBottom:'6px', cursor:'pointer', border:'1px solid #dde3ea',
                  background:'#f8fafd', transition:'all .12s' }}
                onMouseEnter={e => e.currentTarget.style.background='#e8f0fb'}
                onMouseLeave={e => e.currentTarget.style.background='#f8fafd'}>
                <div style={{ fontWeight:'700', color:'#1B2A5E', fontSize:'13px' }}>{p.paciente}</div>
                <div style={{ fontSize:'11px', color:'#6c757d', marginTop:'2px', display:'flex', gap:'12px', flexWrap:'wrap' }}>
                  {p.medico    && <span>🩺 {p.medico}</span>}
                  {p.habitacion && <span>🏠 Hab. {p.habitacion}</span>}
                  {p.dui       && <span>🪪 {p.dui}</span>}
                  <span style={{ color:'#1D9E75', fontWeight:'700' }}>{p.estado}</span>
                </div>
              </div>
            ))}
            <button onClick={() => setModalPac(null)}
              style={{ marginTop:'10px', width:'100%', padding:'9px', background:'#f0f4f8', border:'1px solid #dde3ea', borderRadius:'8px', cursor:'pointer', fontSize:'13px', color:'#495057' }}>
              Cancelar — llenar manualmente
            </button>
          </div>
        </div>
      )}

      {/* ─── MODAL: Reprogramar ─── */}
      {modalRep && (
        <div style={{ position:'fixed', inset:0, background:'rgba(10,20,55,.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setModalRep(null)}>
          <div style={{ background:'#fff', borderRadius:'14px', padding:'28px', width:'440px', boxShadow:'0 24px 64px rgba(0,0,0,.35)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'15px', fontWeight:'800', color:'#c45000', marginBottom:'6px' }}>
              🔄 Reprogramar cirugía
            </div>
            <div style={{ fontSize:'12px', color:'#6c757d', marginBottom:'14px', lineHeight:1.5 }}>
              El registro original quedará en el historial. Ingresa la nueva hora/fecha propuesta.
            </div>

            {/* Fila original */}
            <div style={{ background:'#fff8f0', border:'1px solid #fdba74', borderRadius:'8px', padding:'10px 12px', marginBottom:'14px', fontSize:'11px' }}>
              <div style={{ fontWeight:'700', color:'#c45000', marginBottom:'4px' }}>📋 Registro original</div>
              <div><strong>Hora:</strong> {filas[modalRep.ri]?.[0]}</div>
              <div><strong>Paciente:</strong> {filas[modalRep.ri]?.[2] || '—'}</div>
              <div><strong>Cirugía:</strong> {filas[modalRep.ri]?.[3] || '—'}</div>
              <div><strong>Anotada el:</strong> <span style={{ color:'#1D9E75', fontWeight:'700' }}>{filas[modalRep.ri]?.[CX_TS_IDX] || 'Pendiente de guardar'}</span></div>
            </div>

            <label style={S.label}>Nueva hora / fecha programada</label>
            <input style={{ ...S.input, marginBottom:'16px', textTransform:'uppercase' }}
              placeholder="Ej: VIERNES 9AM / 20-FEB 10:30AM..."
              value={modalRep.nueva_hora}
              onChange={e => setModalRep(r => ({ ...r, nueva_hora: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && confirmarReprog()}
            />

            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={confirmarReprog}
                style={{ flex:1, padding:'11px', background:'#c45000', color:'#fff', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>
                🔄 Confirmar reprogramación
              </button>
              <button onClick={() => setModalRep(null)}
                style={{ padding:'11px 16px', background:'#f0f4f8', border:'1px solid #dde3ea', borderRadius:'8px', cursor:'pointer', fontSize:'13px', color:'#495057' }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Selector de quirófano con tabs ─────────────────────────
function TablaCirugiasTabs({ valor, setValor }) {
  const [tabActivo, setTabActivo] = useState(QUIROFANOS_CONFIG[0].id);
  const qActivo = QUIROFANOS_CONFIG.find(q => q.id === tabActivo) || QUIROFANOS_CONFIG[0];

  return (
    <div style={S.campo}>
      {/* Tabs de quirófanos */}
      <div style={{ display:'flex', gap:'4px', marginBottom:'0', flexWrap:'wrap', borderBottom:`2px solid ${qActivo.color}` }}>
        {QUIROFANOS_CONFIG.map(q => {
          const activo  = q.id === tabActivo;
          const filasK  = `filas_${q.id}`;
          const filasQ  = valor[filasK] || [];
          const conDatos = filasQ.filter(r => Array.isArray(r) && [1,2,3,4,5,7].some(ci => String(r[ci]||'').trim())).length;
          const realizadas  = filasQ.filter(r => Array.isArray(r) && String(r[CX_EST_IDX]||'')==='REALIZADA').length;
          const reprog      = filasQ.filter(r => Array.isArray(r) && String(r[CX_EST_IDX]||'')==='REPROGRAMADA').length;
          return (
            <button key={q.id} onClick={() => setTabActivo(q.id)}
              style={{ padding:'8px 20px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'700',
                borderRadius:'8px 8px 0 0', transition:'all .15s',
                background: activo ? q.color : '#e8f0fb',
                color:      activo ? '#fff'  : '#495057',
                boxShadow:  activo ? `0 -2px 8px ${q.color}40` : 'none' }}>
              🏥 {q.label}
              {conDatos > 0 && (
                <span style={{ marginLeft:'8px', background:'rgba(255,255,255,.3)', color:'#fff',
                  borderRadius:'99px', padding:'1px 8px', fontSize:'10px' }}>
                  {conDatos}
                  {realizadas > 0 && <span style={{ marginLeft:'4px' }}>✅{realizadas}</span>}
                  {reprog > 0     && <span style={{ marginLeft:'4px' }}>🔄{reprog}</span>}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ border:`1px solid ${qActivo.color}`, borderTop:'none', borderRadius:'0 0 8px 8px', padding:'10px', background:'#fff' }}>
        <TablaCirugias dataKey={`filas_${tabActivo}`} valor={valor} setValor={setValor} />
      </div>
    </div>
  );
}

function EditorFormulario({ formulario, paciente, usuario, onCerrar }) {
  const [datos,          setDatos]          = useState({});
  const [guardando,      setGuardando]      = useState(false);
  const [ultimoGuardado, setUltimoGuardado] = useState(null);
  const [msgErr,         setMsgErr]         = useState('');
  const [lockedRows,     setLockedRows]     = useState({});
  // v3: firma+sello del usuario para incrustar en PDF
  const [firmaB64,       setFirmaB64]       = useState(null);

  const id = formulario.id;
  const campos = CAMPOS[id] || [];
  const FORMULARIOS_SOLO_LECTURA = new Set(['consentimiento','ingreso_enfermera','pagare','chequeo_quirurgico']);
  const EGRESO_FIELDS = ['fecha_egreso','hora_egreso','estado_egreso','diagnostico_egreso','procedimiento_realizado','medicamento_alta','obs_egreso'];
  const CAMPOS_BLOQUEABLES = new Set(['ingreso_egreso','consentimiento','ingreso_enfermera','pagare','chequeo_quirurgico']);
  const [fieldLocks, setFieldLocks] = useState({});

  const getAllFieldIds = () => campos.flatMap(sec => (sec.items || []).map(item => item.id));

  const tieneValorUtil = (v) => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  };

  const isIngresoField = (fieldId) => id === 'ingreso_egreso' && !EGRESO_FIELDS.includes(fieldId);
  const isEgresoField = (fieldId) => id === 'ingreso_egreso' && EGRESO_FIELDS.includes(fieldId);
  const egresoGuardado = (src = datos) => EGRESO_FIELDS.some(fid => tieneValorUtil(src?.[fid]));

  const construirLocksDesdeDatos = (src = {}) => {
    const locks = {};
    if (!CAMPOS_BLOQUEABLES.has(id)) return locks;

    if (FORMULARIOS_SOLO_LECTURA.has(id)) {
      const hayDatosGuardados = getAllFieldIds().some(fid => tieneValorUtil(src[fid]));
      if (hayDatosGuardados) getAllFieldIds().forEach(fid => { locks[fid] = true; });
      return locks;
    }

    if (id === 'ingreso_egreso') {
      const ingresoTieneDatos = getAllFieldIds()
        .filter(fid => !EGRESO_FIELDS.includes(fid))
        .some(fid => tieneValorUtil(src[fid]));
      const egresoTieneDatos = EGRESO_FIELDS.some(fid => tieneValorUtil(src[fid]));

      if (ingresoTieneDatos) {
        getAllFieldIds().filter(fid => !EGRESO_FIELDS.includes(fid)).forEach(fid => { locks[fid] = true; });
      }
      if (egresoTieneDatos) {
        EGRESO_FIELDS.forEach(fid => { locks[fid] = true; });
      }
    }

    return locks;
  };

  const isFieldLocked = (fieldId) => !!fieldLocks[fieldId];
  const formularioCompletamenteBloqueado = CAMPOS_BLOQUEABLES.has(id) && getAllFieldIds().every(fid => isFieldLocked(fid));

  // Cargar firma+sello del usuario para el PDF
  useEffect(() => {
    if (!usuario) return;
    const ruta = usuario.firma_sello_ruta || usuario.firma_ruta || null;
    if (!ruta) return;
    // Antes: window.altimo.usuarios.leerFirma({ ruta }) leía el archivo del
    // disco y lo convertía a base64. Ahora la ruta ya es una URL pública.
    setFirmaB64(api.lab.urlArchivo(ruta));
  }, [usuario?.id]);

  // Pre-llenar con datos del paciente + cargar lo guardado previamente
  // IMPORTANTE: los IPC devuelven { ok, data }. Si se intenta leer r.datos directamente,
  // la pantalla nunca rehidrata lo guardado aunque sí exista en la BD.
  useEffect(() => {
    const ahora   = new Date();
    const hoyISO  = ahora.toISOString().slice(0,10);
    const horaHoy = ahora.toTimeString().slice(0,5);

    // 1. Base: datos del expediente + fecha/hora del sistema
    //    Aquí se precargan también aliases para campos repetidos entre formularios
    //    del módulo de admisión, para no obligar a reescribir nombre, DUI,
    //    dirección, teléfonos o aseguradora en cada formato.
    const nombreCompleto = paciente.paciente || '';
    const telefonoBase   = paciente.telefono || '';
    const seguroBase     = paciente.seguro || '';

    const preload = {
      // Datos generales del paciente
      paciente:          nombreCompleto,
      nombre_paciente:   nombreCompleto,
      nombre:            nombreCompleto,
      edad:              paciente.edad || '',
      dui:               paciente.dui || '',
      sexo:              paciente.sexo || '',
      direccion:         paciente.direccion || '',
      nacionalidad:      paciente.nacionalidad || 'Salvadoreño/a',
      telefono:          telefonoBase,
      telefono_casa:     telefonoBase,
      otros_tel:         paciente.otros_tel || paciente.telefono_otro || '',
      aseguradora:       seguroBase,
      seguro:            seguroBase,
      grupo_sanguineo:   paciente.grupo_sanguineo || '',

      // Aliases útiles para documentos del módulo de admisión
      nombre_deudor:     nombreCompleto,
      dui_deudor:        paciente.dui || '',
      nombre_responsable:nombreCompleto,

      // Fechas base
      fecha:             hoyISO,
      fecha_ingreso:     hoyISO,
      hora_ingreso:      horaHoy,
      fecha_registro:    hoyISO,
      lugar_fecha:       `Santa Ana, ${new Date().toLocaleDateString('es-SV')}`,
    };

    // Precargas específicas por formulario
    if (id === 'ingreso_enfermera' && paciente.motivo) {
      preload.motivo_ingreso = paciente.motivo;
    }
    if (id === 'consentimiento') {
      // Si la admisión ya registró responsable legal (paciente menor de edad
      // o el flag esMenor viene de la búsqueda), se precarga todo y se marca
      // automáticamente "Sí" — el usuario en admisión ya hizo esta captura.
      if (paciente.esMenor || paciente.responsable_nombre) {
        preload.es_menor_imposibilitado = 'si';
        preload.nombre_responsable   = paciente.responsable_nombre || '';
        preload.parentesco           = paciente.responsable_parentesco || '';
        preload.tipo_doc_responsable = paciente.responsable_tipo_documento || '';
        preload.num_doc_responsable  = paciente.responsable_dui || '';
      } else {
        preload.es_menor_imposibilitado = 'no';
      }
    }
    if (id === 'pagare') {
      const fechaBasePagare = preload.fecha_pagare || preload.fecha || fechaISOHoy();
      preload.fecha_pagare = fechaBasePagare;
      preload.dia = fechaPagareEnLetras(fechaBasePagare);
      if (preload.monto && !preload.monto_texto) preload.monto_texto = montoDolaresALetras(preload.monto);
      if (preload.monto && preload.cuotas && !preload.monto_cuota) preload.monto_cuota = calcularMontoCuota(preload.monto, preload.cuotas);
    }

    // Auto-fill fecha y hora de llenado para chequeo_quirurgico (solo si no está guardado)
    if (id === 'chequeo_quirurgico' && !preload.fecha_hora_llenado) {
      const ahora = new Date();
      const fh = ahora.toLocaleDateString('es-SV', { day:'2-digit', month:'2-digit', year:'numeric' }) +
                 ' ' + ahora.toLocaleTimeString('es-SV', { hour:'2-digit', minute:'2-digit', hour12: true });
      preload.fecha_hora_llenado = fh;
    }

    setDatos(preload);
    setLockedRows({});
    setFieldLocks({});

    // 2. Si hay admision_id, cargar datos guardados (tienen prioridad)
    const admId = paciente.admision_id;
    if (!admId) return;
    api.formularios.cargar({
      admision_id:  admId,
      formulario_id: id,
    }).then(r => {
      const cargados = r?.ok ? r?.data?.datos : r?.datos;
      if (cargados && typeof cargados === 'object') {
        const fusion = { ...preload, ...cargados };
        if (id === 'pagare') {
          if (fusion.fecha_pagare && !fusion.dia) fusion.dia = fechaPagareEnLetras(fusion.fecha_pagare);
          if (fusion.monto && !fusion.monto_texto) fusion.monto_texto = montoDolaresALetras(fusion.monto);
          if (fusion.monto && fusion.cuotas) fusion.monto_cuota = calcularMontoCuota(fusion.monto, fusion.cuotas);
        }
        setDatos(fusion);
        const nuevosLocks = {};
        Object.entries(cargados).forEach(([k, v]) => {
          if (Array.isArray(v) && v.every(row => Array.isArray(row))) nuevosLocks[k] = v.length;
        });
        setLockedRows(nuevosLocks);
        setFieldLocks(construirLocksDesdeDatos(fusion));
      }
    }).catch(() => {});
  }, [id, paciente.admision_id]);

  // ── Guardar: DB + PDF silencioso en carpeta del paciente ──
  const guardar = async () => {
    const admId = paciente.admision_id;
    if (!admId) { setMsgErr('Carga un expediente (paciente con admisión activa) primero.'); return; }
    if (formularioCompletamenteBloqueado) { setMsgErr('Este formulario ya quedó bloqueado y no puede editarse nuevamente.'); return; }
    setGuardando(true); setMsgErr('');

    // Para chequeo_quirurgico: garantizar que fecha_hora_llenado está en datos antes de guardar
    let datosAGuardar = datos;
    if (id === 'chequeo_quirurgico' && !datos.fecha_hora_llenado) {
      const ahora = new Date();
      const fh = ahora.toLocaleDateString('es-SV', { day:'2-digit', month:'2-digit', year:'numeric' }) +
                 ' ' + ahora.toLocaleTimeString('es-SV', { hour:'2-digit', minute:'2-digit', hour12: true });
      datosAGuardar = { ...datos, fecha_hora_llenado: fh };
      setDatos(datosAGuardar);
    }

    // Para prog_cirugias: auto-stamp Fecha y Hora Anotación en filas con datos nuevos
    // Itera dinámicamente sobre todos los quirófanos configurados
    if (id === 'prog_cirugias') {
      const ahora = new Date();
      const ts = ahora.toLocaleDateString('es-SV', { day:'2-digit', month:'2-digit', year:'numeric' }) +
                 ' ' + ahora.toLocaleTimeString('es-SV', { hour:'2-digit', minute:'2-digit', hour12: true });
      const sellarFilas = (filas) => {
        if (!Array.isArray(filas)) return filas;
        return filas.map(row => {
          if (!Array.isArray(row)) return row;
          const tieneDatos = [1,2,3,4,5,7].some(ci => String(row[ci] || '').trim() !== '');
          if (tieneDatos && !String(row[6] || '').trim()) {
            const nr = [...row]; nr[6] = ts; return nr;
          }
          return row;
        });
      };
      const sellado = {};
      QUIROFANOS_CONFIG.forEach(q => {
        const k = `filas_${q.id}`;
        sellado[k] = sellarFilas(datosAGuardar[k]);
      });
      datosAGuardar = { ...datosAGuardar, ...sellado };
      setDatos(datosAGuardar);
    }

    try {
      const r = await api.formularios.guardar({
        admision_id:   admId,
        formulario_id: id,
        datos: datosAGuardar,
        usuario_id:    usuario.id,
      });
      if (!r.ok) throw new Error(r.error || 'Error al guardar en BD');

      // Generar PDF en disco solo si el formulario tiene GEN
      const gen = GEN[id];
      if (gen) {
        // Pasar firma base64 al generador
        const datosConFirma = firmaB64
          ? { ...datosAGuardar, _firma_b64: firmaB64, _usuario_nombre: usuario.nombre, _usuario_titulo: usuario.titulo || '' }
          : datosAGuardar;
        const htmlParaDisco = gen(paciente, datosConFirma).replace(
          '<script>window.onload=()=>window.print()</script>', ''
        );
        try {
          const rPdf = await api.formularios.guardarPdfDisco({
            admision_id:   admId,
            formulario_id: id,
            html:          htmlParaDisco,
          });
          if (!rPdf.ok) {
            console.warn('[PDF] No se pudo guardar el PDF:', rPdf.error);
            setMsgErr('Datos guardados en BD ✓ — advertencia: el PDF no se generó correctamente (' + (rPdf.error || 'error desconocido') + '). Intenta guardar de nuevo.');
          }
        } catch (ePdf) {
          console.warn('[PDF] Excepción al guardar PDF:', ePdf.message);
          setMsgErr('Datos guardados en BD ✓ — advertencia: el PDF no se generó. Intenta guardar de nuevo.');
        }
      }

      setUltimoGuardado(new Date().toLocaleString('es-SV'));
      setLockedRows(prev => {
        const next = { ...prev };
        Object.entries(datos).forEach(([k, v]) => {
          if (Array.isArray(v) && v.every(row => Array.isArray(row))) next[k] = v.length;
        });
        return next;
      });
      setFieldLocks(construirLocksDesdeDatos(datosAGuardar));
    } catch(e) {
      setMsgErr('Error: ' + e.message);
    }
    setGuardando(false);
  };

  // ── Vista previa: abre ventana con opción de imprimir ──
  const verVistaPrevia = () => {
    const gen = GEN[id];
    if (!gen) return;
    const datosConFirma = firmaB64
      ? { ...datos, _firma_b64: firmaB64, _usuario_nombre: usuario.nombre, _usuario_titulo: usuario.titulo || '' }
      : datos;
    const html = gen(paciente, datosConFirma);
    const w = window.open('', '_blank', 'width=850,height=700');
    w.document.write(html); w.document.close();
  };

  const set = (k, v) => setDatos(prev => {
    const next = { ...prev, [k]: v };
    if (id === 'pagare') {
      if (k === 'fecha_pagare') next.dia = fechaPagareEnLetras(v);
      if (k === 'monto') {
        const autoTexto = montoDolaresALetras(v);
        if (autoTexto) next.monto_texto = autoTexto;
        if (next.cuotas) next.monto_cuota = calcularMontoCuota(v, next.cuotas);
      }
      if (k === 'cuotas') {
        next.monto_cuota = calcularMontoCuota(next.monto, v);
      }
    }
    return next;
  });

  const renderCampo = (campo) => {
    // Para el formulario consentimiento: ocultar campos de responsable si no es menor/imposibilitado
    if (id === 'consentimiento' &&
        ['nombre_responsable','edad_responsable','parentesco',
         'tipo_doc_responsable','num_doc_responsable'].includes(campo.id) &&
        datos['es_menor_imposibilitado'] !== 'si') {
      return null;
    }

    const bloqueado = isFieldLocked(campo.id) || !!campo.readOnlyAuto;
    const inputBaseStyle = {
      ...S.input,
      background: bloqueado ? '#f8fafd' : '#fff',
      color: bloqueado ? '#495057' : '#333',
      cursor: bloqueado ? 'not-allowed' : 'text',
    };

    // Campo especial: fecha y hora de llenado (chequeo_quirurgico)
    if (campo.id === 'fecha_hora_llenado') {
      const yaGuardado = !!datos.fecha_hora_llenado && isFieldLocked(campo.id);
      return (
        <div key={campo.id} style={{ marginBottom:'14px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px',
            background: yaGuardado ? '#fff3cd' : '#e8f0fb',
            border:`1px solid ${yaGuardado ? '#ffc107' : '#c8ddf7'}`,
            borderLeft:`4px solid ${yaGuardado ? '#ff9800' : '#1B2A5E'}`,
            borderRadius:'8px' }}>
            <span style={{ fontSize:'18px' }}>{yaGuardado ? '🔒' : '📋'}</span>
            <div>
              <div style={{ fontSize:'11px', fontWeight:'700', color: yaGuardado ? '#795548' : '#1B2A5E', textTransform:'uppercase', letterSpacing:'.04em' }}>
                {yaGuardado ? 'Formulario bloqueado — solo lectura' : 'Fecha y hora de llenado (automática)'}
              </div>
              <div style={{ fontSize:'13px', fontWeight:'700', color: yaGuardado ? '#e65100' : '#1D9E75', marginTop:'2px' }}>
                {datos.fecha_hora_llenado || '—'}
              </div>
              {yaGuardado && (
                <div style={{ fontSize:'11px', color:'#6c757d', marginTop:'2px' }}>
                  Este chequeo quedó guardado en esta fecha/hora. Por seguridad clínica no puede modificarse.
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (campo.tipo === 'cuotasPagare') {
      const seleccion = ['1','2','3','4','5'].includes(String(datos[campo.id] || '')) ? String(datos[campo.id]) : (datos[campo.id] ? 'mas' : '');
      return (
        <div key={campo.id} style={S.campo}>
          <label style={S.label}>{campo.label}</label>
          <div style={{ display:'grid', gridTemplateColumns: seleccion === 'mas' ? '120px 1fr' : '1fr', gap:'8px' }}>
            <select
              style={inputBaseStyle}
              value={seleccion}
              onChange={e => {
                if (bloqueado) return;
                const val = e.target.value;
                if (val === 'mas') set(campo.id, '6');
                else set(campo.id, val);
              }}
              disabled={bloqueado}>
              <option value="">— Seleccionar —</option>
              <option value="1">1 cuota</option>
              <option value="2">2 cuotas</option>
              <option value="3">3 cuotas</option>
              <option value="4">4 cuotas</option>
              <option value="5">5 cuotas</option>
              <option value="mas">Más...</option>
            </select>
            {seleccion === 'mas' && (
              <input
                style={inputBaseStyle}
                type="number"
                min="6"
                step="1"
                value={datos[campo.id] || ''}
                onChange={e => !bloqueado && set(campo.id, e.target.value)}
                placeholder="Cantidad"
                readOnly={bloqueado}
              />
            )}
          </div>
          <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'4px' }}>
            El monto por cuota se calcula automáticamente según el monto total.
          </div>
        </div>
      );
    }

    if (campo.tipo === 'siNo') {
      return <CampoSiNo key={campo.id} campo={campo} valor={datos} setValor={setDatos} disabled={bloqueado} />;
    }
    if (campo.tipo === 'tabla_dinamica') {
      return <TablaDinamica key={campo.id} campo={campo} valor={datos} setValor={setDatos} usuario={usuario} lockCount={lockedRows[campo.id] || 0} onGuardarFila={guardar} guardandoFila={guardando} />;
    }
    if (campo.tipo === 'tabla_cirugias_multi') {
      return <TablaCirugiasTabs key={campo.id} valor={datos} setValor={setDatos} />;
    }

    if (campo.tipo === 'sinoTexto') {
      const sinoKey = campo.id + '_sino';
      const sinoVal = datos[sinoKey] || '';
      const bloqueadoSino = isFieldLocked(sinoKey) || bloqueado;
      return (
        <div key={campo.id} style={S.campo}>
          <label style={S.label}>{campo.label}</label>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            <div style={{ ...S.sinoWrap, opacity: bloqueadoSino ? 0.75 : 1 }}>
              {['si','no'].map(v => (
                <button key={v}
                  disabled={bloqueadoSino}
                  style={{
                    ...S.sinoBtn(sinoVal === v, v === 'si' ? '#1D9E75' : '#dc3545'),
                    cursor: bloqueadoSino ? 'not-allowed' : 'pointer',
                    opacity: bloqueadoSino ? 0.7 : 1,
                  }}
                  onClick={() => {
                    if (bloqueadoSino) return;
                    setDatos(p => ({
                      ...p, [sinoKey]: v,
                      ...(v === 'no' ? { [campo.id]: '' } : {}),
                    }));
                  }}>
                  {v === 'si' ? 'Sí' : 'No'}
                </button>
              ))}
              {sinoVal === '' && <span style={{ fontSize:'11px', color:'#adb5bd' }}>Seleccionar...</span>}
            </div>
            {sinoVal === 'si' && (
              <input
                style={{ ...inputBaseStyle, borderColor:'#1D9E75' }}
                value={datos[campo.id] || ''}
                onChange={e => !bloqueado && set(campo.id, e.target.value)}
                placeholder="Especifique..."
                autoFocus={!bloqueado}
                readOnly={bloqueado}
              />
            )}
          </div>
          {bloqueadoSino && <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'4px' }}>Registro bloqueado luego de guardarse.</div>}
        </div>
      );
    }

    const inputEl = (() => {
      switch(campo.tipo) {
        case 'textarea':
          return <textarea style={{ ...inputBaseStyle, resize:'vertical', minHeight:`${(campo.rows||4)*22}px` }}
            value={datos[campo.id]||''} onChange={e=>!bloqueado && set(campo.id, e.target.value)} readOnly={bloqueado} />;
        case 'select':
          return <select style={inputBaseStyle} value={datos[campo.id]||''} onChange={e=>!bloqueado && set(campo.id, e.target.value)} disabled={bloqueado}>
            <option value="">— Seleccionar —</option>
            {campo.ops.map(o=><option key={o} value={o}>{o}</option>)}
          </select>;
        default:
          return <input style={inputBaseStyle} type={campo.tipo||'text'} value={datos[campo.id]||''}
            onChange={e=>!bloqueado && set(campo.id, e.target.value)} readOnly={bloqueado} />;
      }
    })();

    return (
      <div key={campo.id} style={S.campo}>
        <label style={S.label}>{campo.label}</label>
        {inputEl}
        {bloqueado && (
          <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'4px' }}>
            Registro bloqueado luego de guardarse.
          </div>
        )}
      </div>
    );
  };

  const renderSeccion = (sec, items) => {
    const colMap = {};
    const sinCol = [];
    items.forEach(c => {
      if (c.col) { if (!colMap[c.col]) colMap[c.col]=[]; colMap[c.col].push(c); }
      else sinCol.push(c);
    });

    const grupos = [];
    let procesados = new Set();
    items.forEach(c => {
      if (procesados.has(c.id)) return;
      if (!c.col) { grupos.push({ tipo:'single', campo:c }); procesados.add(c.id); return; }
      const mismoCol = items.filter(x=>x.col===c.col && !procesados.has(x.id));
      if (mismoCol.length > 0) {
        grupos.push({ tipo:'grid', cols:c.col, campos:mismoCol });
        mismoCol.forEach(x=>procesados.add(x.id));
      }
    });

    return (
      <div key={sec}>
        <div style={S.secH}>{sec}</div>
        {grupos.map((g,i) => {
          if (g.tipo === 'single') return renderCampo(g.campo);
          const gridStyle = { display:'grid', gridTemplateColumns:`repeat(${g.cols},1fr)`, gap:'12px' };
          return <div key={i} style={gridStyle}>{g.campos.map(renderCampo)}</div>;
        })}
      </div>
    );
  };

  return (
    <div style={S.pagina}>
      {/* Header */}
      <div style={{ ...S.card, background:'#1B2A5E', color:'#fff' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:'16px', fontWeight:'800' }}>{formulario.nombre}</div>
            {paciente.paciente && (
              <div style={{ fontSize:'12px', opacity:.8, marginTop:'4px' }}>
                👤 {paciente.paciente} · Exp. #{paciente.expediente}
                {paciente.medico && ` · Dr. ${paciente.medico}`}
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <button style={{ ...S.btnS, background:'#e67e22', color:'#fff', border:'none', fontWeight:'700' }} onClick={onCerrar}>← Volver</button>
            <button style={S.btnV} onClick={verVistaPrevia}>👁 Vista previa</button>
            <button style={S.btnP} onClick={guardar} disabled={guardando || formularioCompletamenteBloqueado}>
              {guardando ? '⏳ Guardando...' : '💾 Guardar'}
            </button>
          </div>
        </div>
        {ultimoGuardado && (
          <div style={{ fontSize:'11px', opacity:.8, marginTop:'6px', display:'flex', alignItems:'center', gap:'6px' }}>
            <span style={{ background:'#1D9E75', borderRadius:'99px', padding:'1px 8px', fontSize:'10px', fontWeight:'700' }}>✓</span>
            Guardado en BD + PDF archivado · {ultimoGuardado}
          </div>
        )}
        {msgErr && <div style={{ background:'#f8d7da', color:'#721c24', padding:'8px 12px', borderRadius:'6px', marginTop:'8px', fontSize:'12px' }}>{msgErr}</div>}
        {CAMPOS_BLOQUEABLES.has(id) && (
          <div style={{ background:'#fff3cd', color:'#856404', padding:'8px 12px', borderRadius:'6px', marginTop:'8px', fontSize:'12px', border:'1px solid #ffe08a' }}>
            {id === 'ingreso_egreso'
              ? (egresoGuardado(datos)
                  ? 'Ingreso y egreso ya fueron guardados. Ambos bloques quedaron protegidos contra edición.'
                  : 'Cuando guardes, el ingreso quedará bloqueado. El egreso seguirá habilitado hasta que se registre y se guarde el alta.')
              : 'Este formulario se bloquea automáticamente después de guardarse para evitar alteraciones posteriores.'}
          </div>
        )}
      </div>

      {/* Datos auto-llenados del paciente */}
      {paciente.paciente && (
        <div style={{ ...S.card, background:'#e8f0fb', border:'1px solid #c8ddf7' }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color:'#1B2A5E', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'10px' }}>
            Datos del paciente (pre-llenados)
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px', fontSize:'12px' }}>
            {[
              ['Nombre',    paciente.paciente],
              ['Expediente',paciente.expediente],
              ['Edad',      paciente.edad],
              ['Sexo',      paciente.sexo],
              ['Médico',    paciente.medico],
              ['Habitación',paciente.habitacion],
              ['Seguro',    paciente.seguro],
              ['DUI',       paciente.dui],
            ].filter(([,v])=>v).map(([l,v])=>(
              <div key={l}>
                <div style={{ fontSize:'10px', fontWeight:'700', color:'#6c757d', textTransform:'uppercase' }}>{l}</div>
                <div style={{ fontWeight:'600', color:'#1B2A5E' }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campos del formulario */}
      <div style={S.card}>
        {campos.length > 0
          ? campos.map(sec => renderSeccion(sec.sec, sec.items))
          : <p style={{ color:'#6c757d', fontSize:'13px' }}>Este formulario no requiere datos adicionales. Genera el PDF directamente.</p>
        }
      </div>

      {/* Botones al final */}
      <div style={{ display:'flex', gap:'10px', alignItems:'center', padding:'4px 0 20px' }}>
        {/* GUARDAR — hace todo: BD + PDF silencioso en carpeta */}
        <button
          style={{ ...S.btnP, padding:'11px 28px', fontSize:'14px', display:'flex', alignItems:'center', gap:'8px' }}
          onClick={guardar} disabled={guardando || formularioCompletamenteBloqueado}>
          {guardando
            ? <><span style={{ width:'14px',height:'14px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'spin .7s linear infinite' }}/>  Guardando...</>
            : <> 💾 Guardar y archivar PDF</>}
        </button>
        {/* VISTA PREVIA — solo abre ventana para ver/imprimir */}
        <button style={{ ...S.btnV, padding:'11px 22px', fontSize:'13px' }} onClick={verVistaPrevia}>
          👁 Vista previa / Imprimir
        </button>
        {/* VOLVER — naranja para distinguirlo claramente */}
        <button
          style={{ background:'#e67e22', color:'#fff', border:'none', borderRadius:'7px', padding:'11px 22px', fontSize:'13px', fontWeight:'700', cursor:'pointer' }}
          onClick={onCerrar}>
          ← Volver al catálogo
        </button>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function Formularios({ usuario, navInicial, onNavConsumida, areaFiltro, excluirForms, pacienteIdInicial }) {
  // ── Estado buscador de paciente (v3: por nombre, DUI o código) ──
  const [textoBusq,    setTextoBusq]    = useState('');
  const [tipoBusq,     setTipoBusq]     = useState('nombre'); // 'nombre' | 'dui' | 'codigo'
  const [resultados,   setResultados]   = useState([]);
  const [buscando,     setBuscando]     = useState(false);
  const [paciente,     setPaciente]     = useState({});
  const [msgErr,       setMsgErr]       = useState('');

  // ── Estado catálogo formularios ──
  const [busqueda,     setBusqueda]     = useState('');
  // Si viene areaFiltro, lo usamos como filtro fijo; si no, "Todos"
  const [filtroArea,   setFiltroArea]   = useState(areaFiltro || 'Todos');
  const [selForm,      setSelForm]      = useState(null);

  // ── Buscar paciente ──────────────────────────────────────────
  const buscarPaciente = async () => {
    const txt = textoBusq.trim();
    if (!txt) return;
    setBuscando(true); setMsgErr(''); setResultados([]);
    try {
      const r = await api.pacientes.buscarFormularios(txt);
      if (r.ok && r.data?.length) {
        setResultados(r.data);
      } else {
        setMsgErr('No se encontró ningún paciente con esos datos.');
      }
    } catch(e) {
      setMsgErr('Error al buscar: ' + e.message);
    }
    setBuscando(false);
  };

  const seleccionarPaciente = (p) => {
    const edadCalc = p.fecha_nac ? calcularEdad(p.fecha_nac) : '';
    // Solo se asocia la admisión si está realmente Activa — una admisión
    // ya dada de alta no debe recibir nuevas prescripciones.
    const admisionActiva = p.admision_estado === 'Activa' ? p.ultima_admision_id : null;
    setPaciente({
      paciente_id: p.id,
      admision_id: admisionActiva,
      medico_id:   admisionActiva ? (p.medico_id || null) : null,
      paciente:    `${p.nombre} ${p.apellido}`,
      codigo:      p.codigo || '',
      expediente:  p.codigo || String(p.id),
      medico:      p.medico_titulo
        ? `${p.medico_titulo} ${p.medico_nombre || ''} ${p.medico_apellido || ''}`.trim()
        : (p.medico_nombre || ''),
      habitacion: p.habitacion_num
        ? `${p.habitacion_num} · ${p.habitacion_tipo}`
        : 'Ambulatorio',
      seguro:       p.seguro_nombre    || '',
      dui:          p.dui              || '',
      telefono:     p.telefono         || '',
      fecha_nac:    p.fecha_nac        || '',
      sexo:         p.sexo === 'M' ? 'Masculino' : p.sexo === 'F' ? 'Femenino' : '',
      edad:         edadCalc,
      esMenor:      edadCalc !== '' && Number(edadCalc) < 18,
      motivo:       p.motivo_ingreso   || p.motivo || '',
      direccion:    p.direccion        || '',
      nacionalidad: p.nacionalidad     || 'Salvadoreño/a',
      grupo_sanguineo: p.grupo_sanguineo || '',
      // Responsable legal capturado en la admisión (solo aplica si es menor/imposibilitado)
      responsable_nombre:     p.responsable_nombre     || '',
      responsable_parentesco: p.responsable_parentesco || '',
      responsable_dui:        p.responsable_dui        || '',
      responsable_tipo_documento: p.responsable_tipo_documento || '',
      responsable_telefono:   p.responsable_telefono   || '',
    });
    setResultados([]);
    setTextoBusq('');
    setMsgErr('');
  };

  const limpiarPaciente = () => {
    setPaciente({}); setTextoBusq(''); setResultados([]); setMsgErr('');
  };

  // ── Precarga cross-tab: si el módulo padre (ej. Admisión) ya tiene un
  // paciente seleccionado en otra pestaña, lo traemos automáticamente al
  // entrar a "Formularios" sin que el usuario tenga que buscarlo de nuevo.
  // Solo se dispara cuando cambia el ID (evita refetch en cada render) y
  // no pisa una selección manual distinta que el usuario ya haya hecho.
  useEffect(() => {
    if (!pacienteIdInicial) return;
    if (paciente?.paciente_id === pacienteIdInicial) return;
    (async () => {
      try {
        const r = await api.pacientes.obtenerFormularios(pacienteIdInicial);
        if (r.ok && r.data) seleccionarPaciente(r.data);
      } catch (e) {
        // Silencioso: si falla, el usuario siempre puede buscarlo manualmente.
      }
    })();
  }, [pacienteIdInicial]);

  // ── Navegación desde otros módulos ──
  useEffect(() => {
    if (navInicial) {
      const f = FORMULARIOS.find(x => x.id === navInicial.formularioId);
      if (f) setSelForm(f);
      if (onNavConsumida) onNavConsumida();
    }
  }, [navInicial]);

  if (selForm) return (
    <EditorFormulario
      formulario={selForm}
      paciente={paciente}
      usuario={usuario}
      onCerrar={() => setSelForm(null)}
    />
  );

  const filtrados = FORMULARIOS.filter(f => {
    if (excluirForms && excluirForms.includes(f.id)) return false;
    const pA = (areaFiltro ? f.area === areaFiltro : filtroArea === 'Todos' || f.area === filtroArea);
    const pB = !busqueda || f.nombre.toLowerCase().includes(busqueda.toLowerCase());
    return pA && pB;
  });

  const TIPO_LABELS = {
    nombre: 'Nombre del paciente',
    dui:    'DUI',
    codigo: 'Código de paciente',
  };

  return (
    <div style={S.pagina}>

      {/* ══ BUSCADOR DE PACIENTE v3 ══ */}
      <div style={S.card}>
        <div style={S.titulo}>
          <span>Identificar paciente para los formularios</span>
          {paciente.paciente && (
            <span style={{ fontSize:'12px', color:'#1D9E75', fontWeight:'700' }}>
              ✓ {paciente.paciente}
              {paciente.codigo && <span style={{ marginLeft:'8px', background:'#e8f0fb', padding:'2px 8px', borderRadius:'4px', fontFamily:'monospace', color:'#1B2A5E' }}>
                {paciente.codigo}
              </span>}
            </span>
          )}
        </div>

        {/* Selector de tipo de búsqueda */}
        <div style={{ display:'flex', gap:'6px', marginBottom:'12px' }}>
          {Object.entries(TIPO_LABELS).map(([val, lbl]) => (
            <button key={val}
              onClick={() => { setTipoBusq(val); setTextoBusq(''); setResultados([]); setMsgErr(''); }}
              style={{
                padding:'6px 14px', borderRadius:'7px', fontSize:'12px', fontWeight:'600',
                cursor:'pointer', border:'none',
                background: tipoBusq === val ? '#1B2A5E' : '#e8f0fb',
                color:      tipoBusq === val ? '#fff'    : '#1B2A5E',
              }}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Campo de búsqueda */}
        <div style={{ display:'flex', gap:'10px', alignItems:'flex-end' }}>
          <div style={{ flex:1 }}>
            <label style={S.label}>{TIPO_LABELS[tipoBusq]}</label>
            <input
              style={S.input}
              value={textoBusq}
              onChange={e => setTextoBusq(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscarPaciente()}
              placeholder={
                tipoBusq === 'nombre'  ? 'Ej: García López, María...' :
                tipoBusq === 'dui'     ? 'Ej: 01234567-8' :
                                         'Ej: 0042/2025'
              }
            />
          </div>
          <button style={S.btnP} onClick={buscarPaciente} disabled={buscando}>
            {buscando ? 'Buscando...' : '🔍 Buscar'}
          </button>
          {paciente.paciente && (
            <button style={S.btnS} onClick={limpiarPaciente}>Limpiar</button>
          )}
        </div>

        {msgErr && (
          <div style={{ marginTop:'8px', color:'#721c24', fontSize:'12px', background:'#f8d7da', padding:'6px 10px', borderRadius:'6px' }}>
            {msgErr}
          </div>
        )}

        {/* Lista de resultados */}
        {resultados.length > 0 && (
          <div style={{ marginTop:'12px', border:'1px solid #dde3ea', borderRadius:'8px', overflow:'hidden' }}>
            <div style={{ background:'#f8fafd', padding:'8px 12px', fontSize:'11px', fontWeight:'700', color:'#6c757d', textTransform:'uppercase' }}>
              {resultados.length} paciente{resultados.length !== 1 ? 's' : ''} encontrado{resultados.length !== 1 ? 's' : ''} — doble clic o botón para seleccionar
            </div>
            {resultados.map(p => (
              <div key={p.id}
                onDoubleClick={() => seleccionarPaciente(p)}
                style={{ padding:'10px 14px', borderTop:'1px solid #eef1f6', display:'flex', justifyContent:'space-between', alignItems:'center', background: paciente?.paciente_id === p.id ? '#e8f5e9' : 'transparent' }}
                onMouseEnter={e => { if (paciente?.paciente_id !== p.id) e.currentTarget.style.background = '#f0f4f8'; }}
                onMouseLeave={e => { if (paciente?.paciente_id !== p.id) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ flex:1, cursor:'default' }}>
                  <span style={{ fontWeight:'700', color:'#1B2A5E', marginRight:'10px' }}>
                    {p.nombre} {p.apellido}
                  </span>
                  <span style={{ fontSize:'11px', color:'#6c757d' }}>
                    {p.dui ? `DUI: ${p.dui}` : 'Sin DUI'}
                    {p.telefono ? ` · Tel: ${p.telefono}` : ''}
                  </span>
                  <div style={{ fontSize:'11px', color:'#6c757d', marginTop:'2px' }}>
                    {p.fecha_nac ? `Nac: ${p.fecha_nac}` : ''}{p.nacionalidad ? ` · ${p.nacionalidad}` : ''}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'8px', alignItems:'center', flexShrink:0 }}>
                  {p.codigo && (
                    <span style={{ fontFamily:'monospace', fontSize:'11px', background:'#e8f0fb', padding:'2px 8px', borderRadius:'4px', color:'#1B2A5E', fontWeight:'700' }}>
                      {p.codigo}
                    </span>
                  )}
                  {p.admision_estado === 'Activa' && (
                    <span style={{ fontSize:'10px', background:'#d4edda', color:'#155724', padding:'2px 8px', borderRadius:'99px', fontWeight:'700' }}>
                      Ingresado
                    </span>
                  )}
                  <button
                    onClick={() => seleccionarPaciente(p)}
                    style={{ background:'#1B2A5E', color:'#fff', border:'none', borderRadius:'6px', padding:'6px 14px', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
                    Seleccionar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info del paciente seleccionado */}
        {paciente.paciente && (
          <div style={{ marginTop:'12px' }}>
            <div style={{
              display:'flex', justifyContent:'space-between', alignItems:'center', gap:'10px', flexWrap:'wrap',
              background:'#f8fafd', border:'1px solid #cfe0f5', borderRadius:'8px', padding:'10px 12px', marginBottom:'8px'
            }}>
              <div>
                <div style={{ fontSize:'10px', color:'#6c757d', fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em' }}>Paciente seleccionado</div>
                <div style={{ fontSize:'15px', fontWeight:'800', color:'#1B2A5E', marginTop:'2px' }}>{paciente.paciente}</div>
              </div>
              {paciente.codigo && (
                <div style={{ fontFamily:'monospace', fontSize:'12px', background:'#e8f0fb', padding:'4px 10px', borderRadius:'6px', color:'#1B2A5E', fontWeight:'800' }}>
                  {paciente.codigo}
                </div>
              )}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,minmax(140px,1fr))', gap:'8px' }}>
              {[
                { l:'Nombre del paciente', v: paciente.paciente, span: 2, force: true },
                { l:'Código',             v: paciente.codigo,                 force: true },
                { l:'Edad',               v: paciente.edad,                   force: true },
                { l:'Sexo',               v: paciente.sexo,                   force: true },
                { l:'DUI',                v: paciente.dui },
                { l:'Médico',             v: paciente.medico },
                { l:'Habitación',         v: paciente.habitacion },
                { l:'Seguro',             v: paciente.seguro },
                { l:'Teléfono',           v: paciente.telefono },
              ].filter(x => x.force || x.v).map(({ l, v, span }) => (
                <div key={l} style={{ background:'#f8fafd', border:'1px solid #e8f0fb', borderRadius:'6px', padding:'6px 10px', gridColumn: span ? `span ${span}` : 'span 1' }}>
                  <div style={{ fontSize:'10px', color:'#6c757d', fontWeight:'600', textTransform:'uppercase' }}>{l}</div>
                  <div style={{ fontSize:'12px', fontWeight:'600', color:'#1B2A5E', marginTop:'2px' }}>{v || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p style={{ fontSize:'11px', color:'#adb5bd', marginTop:'10px' }}>
          Sin paciente seleccionado, los formularios se imprimen en blanco para llenado manual.
        </p>
      </div>

      {/* ══ PRESCRIPCIONES (solo médicos, con paciente + admisión seleccionados) ══ */}
      {areaFiltro === 'clinico' && paciente.paciente && (
        paciente.admision_id ? (
          <PrescripcionesPanel
            admision_id={paciente.admision_id}
            medico_id={paciente.medico_id || null}
            usuario={usuario}
          />
        ) : (
          <div style={S.card}>
            <div style={S.titulo}><span>💊 Prescripciones de Medicamentos</span></div>
            <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
              Este paciente no tiene una admisión activa registrada. Solo se pueden recetar medicamentos a pacientes hospitalizados.
            </div>
          </div>
        )
      )}

      {/* ══ ÓRDENES DE LABORATORIO (solo médicos, con paciente + admisión seleccionados) ══ */}
      {areaFiltro === 'clinico' && paciente.paciente && (
        paciente.admision_id ? (
          <OrdenesLabPanel
            admision_id={paciente.admision_id}
            medico_id={paciente.medico_id || null}
            usuario={usuario}
          />
        ) : (
          <div style={S.card}>
            <div style={S.titulo}><span>🧪 Estudios Clínicos</span></div>
            <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
              Este paciente no tiene una admisión activa registrada. Solo se pueden solicitar exámenes a pacientes hospitalizados.
            </div>
          </div>
        )
      )}

      {/* ══ CATÁLOGO DE FORMULARIOS ══ */}
      <div style={S.card}>
        <div style={S.titulo}><span>Formularios ({filtrados.length} de {FORMULARIOS.length})</span></div>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'16px', alignItems:'center' }}>
          <input style={{ ...S.input, maxWidth:'240px' }} placeholder="Buscar formulario..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          {!areaFiltro && ['Todos', ...AREAS.map(a => a.id)].map(a => {
            const area = AREAS.find(x => x.id === a);
            const activo = filtroArea === a;
            return (
              <button key={a} onClick={() => setFiltroArea(a)}
                style={{ padding:'5px 12px', borderRadius:'7px', fontSize:'11px', fontWeight:'600',
                  cursor:'pointer', border:'none',
                  background: activo ? (area?.color || '#1B2A5E') : '#e8f0fb',
                  color:      activo ? '#fff' : '#1B2A5E' }}>
                {area?.label || 'Todos'}
              </button>
            );
          })}
        </div>

        {AREAS.filter(a => filtroArea === 'Todos' || a.id === filtroArea).map(area => {
          const items = filtrados.filter(f => f.area === area.id);
          if (!items.length) return null;
          return (
            <div key={area.id} style={{ marginBottom:'18px' }}>
              <div style={{ ...S.aH, background: area.color }}>{area.label} ({items.length})</div>
              <div style={S.fgrid}>
                {items.map(f => (
                  <div key={f.id} style={S.fcard(area.color)} onClick={() => setSelForm(f)}
                    onMouseEnter={e => { e.currentTarget.style.transform='scale(1.02)'; e.currentTarget.style.boxShadow='0 4px 14px rgba(0,0,0,.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='none'; }}>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:'#1B2A5E', marginBottom:'4px' }}>{f.nombre}</div>
                    <div style={{ fontSize:'10px', color:'#6c757d', marginBottom:'8px' }}>{area.label}</div>
                    <button
                      style={{ background: area.color, color:'#fff', border:'none', borderRadius:'6px', padding:'5px 12px', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}
                      onClick={e => { e.stopPropagation(); setSelForm(f); }}>
                      Abrir y llenar →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

